/**
 * Customer Portal API Client
 * Handles authentication and API calls to the BAF Customer Portal API
 * Enhanced with JWT token authentication for APIM
 * 
 * ALL API CALLS GO THROUGH CENTRALIZED SERVICE WITH PROGRESSIVE BACKOFF
 * 
 * Every API call (via apiRequest, get, post, put, delete, patch, and all high-level methods)
 * automatically uses the centralized API service which provides:
 * 
 * - Progressive Exponential Backoff: Exponential delays with jitter (1s → 2s → 4s → 8s → max 30s)
 * - Circuit Breaker Pattern: Opens after 5 failures, 1-minute timeout before retry
 * - Request Deduplication: Prevents duplicate calls within 1-second window
 * - Graceful Error Handling: Container-missing errors don't count as failures
 * - Retry Logic: Automatically retries retryable errors (5xx, network errors) up to 3 times
 * - Prevents Infinite Loops: Stops request storms and prevents looping
 * 
 * This ensures all API calls are resilient, efficient, and won't cause looping issues.
 */

// Performance optimization imports (frontend does not cache; backend uses Redis)
import { getApiEndpoint } from './centralizedConfig';
import { getApiService } from './apiService';
import { generateRequestName } from './requestNaming';
import { updateInflight } from './inflightTracker';
import { getOrSelectActiveAccount } from './msalConfig';
import { authLogger } from './authLogger';
import { isLegalPage } from './legalAgreementsGuard';
import { normalizeLegalAgreementsFromApi } from './normalizeLegalAgreements';
import { queueRequest, getPriorityFromEndpoint } from './requestQueue';
// wsClient is loaded only when msalInstance is set (auth site); platform-public never loads it (lean bundle)
import logger from './logger';
import { parseHttpJsonResponseBody } from './parseHttpJsonResponseBody';
import { getSlowFetchWarnThresholdMs } from './slowRequestThresholds';
import { getApiUserMessage } from './apiClientUserMessages';

/** @returns {string|null} */
function getContentTypeFromHeaders(h) {
  if (!h || typeof h !== 'object') return null;
  const key = Object.keys(h).find((k) => k.toLowerCase() === 'content-type');
  return key != null ? h[key] : null;
}

function buildFetchBodyAndContentType(body) {
  if (body == null) {
    return { fetchBody: undefined, contentType: null, omitContentType: true };
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return { fetchBody: body, contentType: null, omitContentType: true };
  }
  if (typeof body === 'string') {
    return { fetchBody: body, contentType: 'text/plain;charset=utf-8', omitContentType: false };
  }
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return { fetchBody: body, contentType: 'application/octet-stream', omitContentType: false };
  }
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView && ArrayBuffer.isView(body)) {
    return { fetchBody: body, contentType: 'application/octet-stream', omitContentType: false };
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return { fetchBody: body, contentType: 'application/octet-stream', omitContentType: false };
  }
  return { fetchBody: JSON.stringify(body), contentType: 'application/json', omitContentType: false };
}

/** Trace auth flow in console — filter by [AUTH] to see exact path. */
function authTrace(tag, detail) {
  const msg = detail != null ? `[AUTH] ${tag}` : `[AUTH] ${tag}`;
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(msg, detail != null ? detail : '');
  }
}

/** Session storage key for active impersonation (userId, tenantId, requestId, targetEmail). */
export const IMPERSONATION_STORAGE_KEY = 'besper_impersonation';

/**
 * @returns {{ userId: string, tenantId: string, requestId: string, targetEmail?: string } | null}
 */
export function getActiveImpersonation() {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (data && data.userId && data.tenantId && data.requestId) return data;
    } catch (_) {}
    return null;
}

/**
 * @param {{ userId: string, tenantId: string, requestId: string, targetEmail?: string }} session
 */
export function setActiveImpersonation(session) {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(session));
        window.dispatchEvent(new CustomEvent('impersonation-changed', { detail: session }));
    } catch (_) {}
}

export function clearImpersonation() {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('impersonation-changed', { detail: null }));
    } catch (_) {}
}

// API Configuration
const getApiConfig = async () => {
    // Use centralized configuration - now pointing to customer portal APIM
    const baseUrl = await getApiEndpoint('customer-portal');
    
    return {
        baseUrl,
        timeout: 30000,
        retryAttempts: 1,
        retryDelay: 1000
    };
};

// Public APIM Configuration (bot management, payment)
const getPublicApiConfig = async () => {
    const baseUrl = await getApiEndpoint('public');
    return {
        baseUrl,
        timeout: 30000,
        retryAttempts: 1,
        retryDelay: 1000
    };
};

/**
 * API Client Class
 * Manages authenticated requests to the Customer Portal API with JWT tokens
 */
class CustomerPortalApiClient {
    constructor() {
        this.config = null;
        this.msalInstance = null;
        this.initialized = false;
        this._logoutInProgress = false; // Flag to prevent multiple simultaneous logout attempts
        this.initPromise = null;
        this.requestCache = new Map(); // Cache for deduplicating requests
        this.cacheTimeout = 1000; // 1 second cache timeout (reduced from 5 seconds)
        this.apiService = getApiService(); // Centralized API service with progressive backoff
        // Authentication coordination state - shared across concurrent requests
        this.authReadyPromise = null; // Shared promise for authentication readiness check
        this.authReady = false; // Cached authentication ready state
        // MSAL initialization promise - ensures MSAL is set before making requests
        this.msalInitPromise = null;
        this.msalInitResolve = null;
        // In-flight request deduplication - tracks pending requests to prevent duplicate calls
        this.pendingRequests = new Map();
        // Memory cache with TTL for user data (10 seconds)
        this.memoryCache = new Map();
        this.CACHE_TTL_MS = 10000; // 10 seconds
        
        // Token cache to prevent blocking requests
        this.tokenCache = {
            token: null,
            expiresAt: null,
            refreshPromise: null, // Prevents concurrent refresh attempts
            lastRefreshAttempt: null
        };
        this.TOKEN_REFRESH_BUFFER_MS = 60000; // Refresh 1 minute before expiration
        this.TOKEN_REFRESH_COOLDOWN_MS = 5000; // Don't refresh more than once per 5 seconds
        this._entraLoginRedirectTriggered = false;
        // Re-entry guard: prevents "Maximum call stack size exceeded" when token acquisition
        // triggers a synchronous path that calls getApiAccessToken again (e.g. MSAL events → React render)
        this._getApiAccessTokenDepth = 0;
    }

    /**
     * If user has an account but no token (e.g. MSAL not yet restored after navigation),
     * wait briefly and retry token once to avoid "random" redirect to public.
     * @returns {Promise<string|null>} Token if obtained, else null
     */
    async _retryTokenOnceIfAccountExists() {
        const account = getOrSelectActiveAccount(this.msalInstance);
        if (!account || !this.msalInstance) return null;
        logger.debug('API-CLIENT', 'Account exists but no token - waiting briefly for MSAL to restore session');
        await new Promise(r => setTimeout(r, 1500));
        const token = await this.getApiAccessToken();
        if (token && typeof token === 'string' && token.trim().length > 0) {
            logger.info('API-CLIENT', 'Got token after MSAL-restore retry - avoiding redirect to public');
            return token;
        }
        return null;
    }

    /**
     * When no valid token is available, log out and send user to unauth (platform-public) so they can log in properly.
     * Avoids Entra redirect that can hit AADSTS160021 (session does not exist). Only triggers once per page load.
     * On platform-public this is a no-op: no authentication is required there.
     */
    _redirectToEntraLoginIfAccountExists() {
        if (this._entraLoginRedirectTriggered) return;
        import('./getPublicDomain').then(({ isOnPlatformAuth }) => {
            if (!isOnPlatformAuth()) {
                // We are on platform-public (the public site). No login needed — just fail silently.
                logger.warn('API-CLIENT', 'No valid token on platform-public; skipping auto-logout (public site, no auth required)');
                return;
            }
            this._entraLoginRedirectTriggered = true;
            import('./authRecovery').then(({ performAutoLogout }) => {
                logger.info('API-CLIENT', 'No valid token - logging out and sending to unauth page so user can log in');
                performAutoLogout('no_valid_token');
            }).catch((importErr) => {
                logger.error('API-CLIENT', 'Failed to import authRecovery', { error: importErr?.message });
                this._entraLoginRedirectTriggered = false;
            });
        }).catch(() => {
            // Cannot determine platform — skip redirect to be safe.
        });
    }
    
    /**
     * Check if MSAL has an interaction in progress by checking localStorage
     * This is a backup check when getInteractionInProgress is not available
     * @returns {boolean} True if interaction is in progress
     */
    _checkMsalInProgress() {
        try {
            // CRITICAL: First check if URL contains auth callback parameters
            // This is the most reliable indicator that a redirect is being processed
            const hasCode = window.location.hash.includes('code=') || window.location.search.includes('code=');
            const hasError = window.location.hash.includes('error=') || window.location.search.includes('error=');
            const hasState = window.location.hash.includes('state=') || window.location.search.includes('state=');
            
            if (hasCode || hasError || hasState) {
                // URL contains auth callback parameters - MSAL is definitely processing a redirect
                return true;
            }
            
            // Check localStorage for MSAL interaction status
            const interactionStatus = localStorage.getItem('msal.interaction.status');
            
            // If there's an active interaction status that isn't 'None', interaction is in progress
            if (interactionStatus && interactionStatus !== 'None' && interactionStatus !== 'null') {
                return true;
            }
            
            // Also check for any pending requests in localStorage (MSAL stores request state here)
            const msalKeys = Object.keys(localStorage).filter(k => 
                k.includes('msal') && (k.includes('request') || k.includes('interaction'))
            );
            
            // If there are request/interaction keys with recent timestamps, interaction may be in progress
            for (const key of msalKeys) {
                try {
                    const value = localStorage.getItem(key);
                    if (value) {
                        const parsed = JSON.parse(value);
                        // Check if this is a recent interaction (within last 5 minutes)
                        if (parsed.timestamp) {
                            const age = Date.now() - parsed.timestamp;
                            if (age < 5 * 60 * 1000) { // 5 minutes
                                return true;
                            }
                        }
                    }
                } catch {
                    // Skip keys that can't be parsed
                }
            }
            
            return false;
        } catch (error) {
            logger.warn('API-CLIENT', 'Error checking MSAL interaction status', { error: error.message });
            return false;
        }
    }

    async ensureInitialized() {
        if (this.initialized) {
            return;
        }
        
        if (this.initPromise) {
            return this.initPromise;
        }
        
        this.initPromise = this.initializeConfig();
        await this.initPromise;
        this.initialized = true;
    }

    async initializeConfig() {
        try {
            // Wait for centralized config to be ready
            const { centralizedConfig } = await import('./centralizedConfig');
            await centralizedConfig.waitForInitialization();
            this.config = await getApiConfig();
            // Platform-public (msalInstance null): skip WebSocket entirely for lean bundle
            if (this.msalInstance && !this._wsTokenProviderSet) {
                this._wsTokenProviderSet = true;
                const { setWsTokenProvider } = await import('./wsClient');
                const self = this;
                setWsTokenProvider(() => self.getApiAccessToken());
            }
        } catch (error) {
            logger.error('API-CLIENT', 'Failed to initialize configuration', { error: error.message });
            throw error;
        }
    }

    /**
     * Build a token request for MSAL based on the configured API scope.
     * @param {string|undefined} scope - API scope from configuration
     * @param {object} account - MSAL account
     * @param {boolean} forceRefresh - Whether to force refresh the token
     * @returns {object} Token request object
     */
    buildTokenRequest(scope, account, forceRefresh = false) {
        // BEST PRACTICE: For API access tokens, only include the API scope
        // OIDC scopes (openid, profile, email) are already in the ID token from login
        // Including them in access token requests increases chances of interaction_required errors (AADSTS160021)
        // MSAL best practice: request only the resource scope for access tokens
        
        // For Entra External ID, we need to pass the user flow name as the 'p' query parameter
        // CRITICAL: Do NOT use flow-scoped authority (path-based) as it breaks OpenID discovery
        // Instead, use the base authority and pass the flow name via extraQueryParameters
        const userFlowName = window._env_?.ENTRA_USER_FLOW_NAME || window.__msal_user_flow_name || 'b-esper_signup';
        
        // If API scope is provided, use only that scope (best practice for API access tokens)
        if (scope && scope.includes('api://')) {
            const request = {
                scopes: [scope], // Only API scope - OIDC scopes not needed for access tokens
                account: account,
                forceRefresh: forceRefresh,
                extraQueryParameters: {
                    p: userFlowName    // User flow name as query parameter (Entra External ID requirement)
                }
            };
            
            authLogger.debug('[API Client] Using base authority for token request with p parameter', { userFlowName });
            return request;
        }
        
        // If no API scope configured, we cannot make API calls
        // This should not happen in production - API scope should always be configured
        logger.error('API-CLIENT', 'No API scope provided to buildTokenRequest');
        const request = {
            scopes: [], // Empty scopes will cause MSAL to fail gracefully
            account: account,
            forceRefresh: forceRefresh,
            extraQueryParameters: {
                p: userFlowName    // User flow name as query parameter (Entra External ID requirement)
            }
        };
        
        return request;
    }

    /**
     * Determine if the encountered error warrants a forced refresh.
     * @param {Error} error
     * @returns {boolean}
     */
    shouldForceRefresh(error) {
        if (!error) {
            return false;
        }
        const message = (error.message || '').toLowerCase();
        return error.name === 'InteractionRequiredAuthError' ||
            message.includes('expired') ||
            message.includes('invalid token') ||
            message.includes('refresh token') ||
            message.includes('interaction required');
    }

    /**
     * Detect errors caused by silent token iframe being blocked (sandbox / allow-top-navigation).
     * When the IdP loads in MSAL's hidden iframe and tries to redirect, the browser blocks it.
     * Fallback: acquireTokenRedirect so the user gets a token via full-page redirect.
     * @param {Error} error
     * @returns {boolean}
     */
    isSilentIframeBlockedError(error) {
        if (!error) return false;
        const code = (error.errorCode || '').toLowerCase();
        const message = (error.message || error.errorMessage || '').toLowerCase();
        return (
            code === 'monitor_window_timeout' ||
            code === 'block_iframe_reload' ||
            message.includes('allow-top-navigation') ||
            message.includes('sandbox') ||
            message.includes('unsafe attempt') ||
            message.includes('block_iframe_reload')
        );
    }

    /**
     * Initialize the API client with MSAL instance
     * @param {object} msalInstance - MSAL instance for authentication
     */
    initialize(msalInstance) {
        this.msalInstance = msalInstance;

        // Reset authentication state when MSAL instance changes
        this.authReady = false;
        this.authReadyPromise = null;

        // Resolve MSAL initialization promise if it exists
        if (this.msalInitResolve) {
            this.msalInitResolve();
            this.msalInitResolve = null;
            this.msalInitPromise = null;
        }

        // Platform-public (msalInstance null): no WebSocket, no token refresh — lean bundle
        if (!msalInstance) {
            return;
        }

        // Auth site only: set WebSocket token provider and start token refresh
        if (!this._wsTokenProviderSet) {
            this._wsTokenProviderSet = true;
            import('./wsClient').then(({ setWsTokenProvider, connectWs }) => {
                const self = this;
                setWsTokenProvider(() => self.getApiAccessToken());
                connectWs().catch(() => {});
            }).catch(() => {});
        }
        this.startPeriodicTokenRefresh();
    }
    
    /**
     * Wait for MSAL instance to be initialized
     * Returns immediately if already initialized, otherwise waits for initialization
     * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 5000)
     * @returns {Promise<boolean>} True if MSAL instance is available
     */
    async waitForMsalInstance(maxWaitTime = 5000) {
        // If MSAL instance already exists, return immediately
        if (this.msalInstance) {
            return true;
        }

        // On platform-public, MSAL is intentionally not initialized (public site, no login).
        // Skip the full timeout — return false immediately so callers fail fast without delay.
        try {
            const { isOnPlatformAuth } = await import('./getPublicDomain');
            if (!isOnPlatformAuth()) {
                return false;
            }
        } catch (_) {}
        
        // If initialization promise doesn't exist, create it
        if (!this.msalInitPromise) {
            this.msalInitPromise = new Promise((resolve) => {
                this.msalInitResolve = resolve;
            });
        }
        
        // Wait for initialization with timeout
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(() => resolve(false), maxWaitTime);
        });
        
        const result = await Promise.race([
            this.msalInitPromise.then(() => true),
            timeoutPromise
        ]);
        
        if (!result) {
            logger.warn('API-CLIENT', 'MSAL instance initialization timeout', { maxWait_ms: maxWaitTime });
        }
        
        return result;
    }

    /**
     * Start periodic token refresh to prevent expiration
     * DISABLED: Causing refresh loops - tokens will be refreshed on-demand instead
     */
    startPeriodicTokenRefresh() {
        // DISABLED - was causing aggressive refresh loops
        // Tokens will be refreshed on-demand when needed instead
        // Clear any existing interval
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
        
        // Periodic refresh disabled to prevent loops
        // Token refresh will happen on-demand when API calls are made
    }

    /**
     * Stop periodic token refresh
     */
    stopPeriodicTokenRefresh() {
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
    }

    /**
     * Get the base URL for API requests
     * @returns {Promise<string>} Base URL
     */
    async getBaseUrl() {
        await this.ensureInitialized();
        return this.config.baseUrl;
    }

    /**
     * Check if a request is already in progress to prevent duplicates
     * @param {string} url - Request URL
     * @param {object} options - Request options
     * @returns {boolean} True if request is already in progress
     */
    isRequestInProgress(url, options = {}) {
        const cacheKey = `${options.method || 'GET'}:${url}:${JSON.stringify(options.body || {})}`;
        const cached = this.requestCache.get(cacheKey);
        
        if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
            return true;
        }
        
        // Store the request
        this.requestCache.set(cacheKey, { timestamp: Date.now() });
        
        // Clean up old entries
        for (const [key, value] of this.requestCache.entries()) {
            if ((Date.now() - value.timestamp) > this.cacheTimeout) {
                this.requestCache.delete(key);
            }
        }
        
        return false;
    }

    /**
     * Get access token for API requests (alias for getApiAccessToken with caching)
     * @returns {Promise<string|null>} Access token or null if MSAL not initialized
     */
    async getAccessToken() {
        // Use the cached version which prevents blocking
        return this.getApiAccessToken();
    }

    /**
     * Check if current token is close to expiring and refresh if needed
     * @returns {Promise<boolean>} True if token was refreshed
     */
    async ensureFreshToken() {
        // DISABLED - was causing aggressive refresh loops
        // Only refresh tokens when explicitly needed (on API calls), not proactively
        // This prevents the refresh loop that was breaking authentication
        return false;
    }

    /**
     * Create authenticated headers for API requests
     * @returns {Promise<object>} Headers object with Authorization bearer token
     */
    async getAuthenticatedHeaders() {
        const token = await this.getAccessToken();
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };
    }

    /**
     * Get authentication token for API requests (enhanced version with caching)
     * Returns cached token immediately if available and valid, otherwise acquires new token
     * @param {object} [options] - Optional. { skipCallbackCheck: true } when called from auth callback to warm token
     * @returns {Promise<string|null>} Access token or null
     */
    async getApiAccessToken(options = {}) {
        const skipCallbackCheck = options.skipCallbackCheck === true;
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        authTrace('getApiAccessToken ENTRY', { skipCallbackCheck, pathname, hashLen: hash.length });

        // Re-entry guard: break synchronous recursion (e.g. MSAL setActiveAccount → event → render → getApiAccessToken again)
        this._getApiAccessTokenDepth = (this._getApiAccessTokenDepth || 0) + 1;
        if (this._getApiAccessTokenDepth > 1) {
            this._getApiAccessTokenDepth--;
            if (this.tokenCache.token) {
                authTrace('getApiAccessToken RE-ENTRY → return cached token');
                return this.tokenCache.token;
            }
            authTrace('getApiAccessToken RE-ENTRY → null');
            logger.debug('API-CLIENT', 'getApiAccessToken re-entry detected, returning null to break recursion');
            return null;
        }
        try {
        // FAST PATH: Return cached token if available and not expired
        const now = Date.now();
        if (this.tokenCache.token && 
            this.tokenCache.expiresAt && 
            this.tokenCache.expiresAt > now + this.TOKEN_REFRESH_BUFFER_MS) {
            authTrace('getApiAccessToken FAST PATH → cached token valid');
            return this.tokenCache.token;
        }
        
        // If refresh is already in progress, wait for it instead of starting a new one
        if (this.tokenCache.refreshPromise) {
            authTrace('getApiAccessToken WAIT existing refreshPromise');
            try {
                const token = await this.tokenCache.refreshPromise;
                authTrace('getApiAccessToken refreshPromise resolved', { gotToken: !!token });
                return token;
            } catch (err) {
                authTrace('getApiAccessToken refreshPromise rejected', { err: err?.message });
            }
        }
        
        // Check cooldown to prevent excessive refresh attempts
        if (this.tokenCache.lastRefreshAttempt && 
            (now - this.tokenCache.lastRefreshAttempt) < this.TOKEN_REFRESH_COOLDOWN_MS) {
            if (this.tokenCache.token) {
                authTrace('getApiAccessToken COOLDOWN → return cached token');
                this._refreshTokenInBackground();
                return this.tokenCache.token;
            }
        }
        
        if (!this.msalInstance) {
            authTrace('getApiAccessToken SKIP → no msalInstance');
            return null;
        }
        
        // CRITICAL: Check if we're on the callback page FIRST - don't start new interactions
        const hasCode = window.location.hash.includes('code=') || window.location.search.includes('code=');
        const hasError = window.location.hash.includes('error=') || window.location.search.includes('error=');
        const hasState = window.location.hash.includes('state=') || window.location.search.includes('state=');
        const isCallbackPage = window.location.pathname === '/auth/callback' || hasCode || hasError || hasState;
        
        if (!skipCallbackCheck && isCallbackPage) {
            authTrace('getApiAccessToken SKIP CALLBACK PAGE', { pathname: window.location.pathname, hasCode, hasError, hasState });
            return null;
        }
        
        const inProgress = skipCallbackCheck ? false : (this._checkMsalInProgress() ||
                          this.msalInstance.getInteractionInProgress?.());
        
        if (!skipCallbackCheck && inProgress) {
            authTrace('getApiAccessToken SKIP MSAL IN PROGRESS', { inProgress: !!inProgress, pathname: window.location.pathname });
            if (this.tokenCache.token) return this.tokenCache.token;
            return null;
        }
        
        let account = getOrSelectActiveAccount(this.msalInstance);
        
        if (!account) {
            const allAccounts = this.msalInstance.getAllAccounts();
            authTrace('getApiAccessToken SKIP NO ACCOUNT', { accountsCount: allAccounts?.length ?? 0 });
            if (allAccounts && allAccounts.length > 1) {
                return null;
            }
            return null;
        }
        
        const scope = (window._env_ && window._env_.ENTRA_API_SCOPE) || (process.env.REACT_APP_ENTRA_API_SCOPE);
        const tokenRequest = this.buildTokenRequest(scope, account, false);

        const isExpired = (expiresOn) => {
            if (!expiresOn) return false;
            return new Date(expiresOn).getTime() <= Date.now();
        };

        const acquireTokenSilent = async () => {
            let response = await this.msalInstance.acquireTokenSilent(tokenRequest);
            if (isExpired(response.expiresOn)) {
                response = await this.msalInstance.acquireTokenSilent({
                    ...tokenRequest,
                    forceRefresh: true
                });
            }
            return response;
        };

        authTrace('getApiAccessToken CALLING acquireTokenSilent', { account: account?.username, scope: !!scope });

        // Start refresh promise to prevent concurrent requests
        this.tokenCache.refreshPromise = (async () => {
            try {
                this.tokenCache.lastRefreshAttempt = now;
                const response = await acquireTokenSilent();
            
                // CRITICAL: Validate response is an object with expected properties
                if (!response || typeof response !== 'object' || Array.isArray(response)) {
                    logger.error('API-CLIENT', 'acquireTokenSilent returned invalid response', {
                        responseType: typeof response,
                        isArray: Array.isArray(response),
                        response: response
                    });
                    this.tokenCache.refreshPromise = null;
                    return null;
                }
                
                // For API calls, we MUST use access token, not ID token
                // If scope is provided, we need accessToken; if no scope, we can't make API calls anyway
                if (scope) {
                    const accessToken = response.accessToken;
                    
                    // CRITICAL: Validate accessToken is a string, not an object or array
                    if (accessToken !== undefined && accessToken !== null) {
                        if (typeof accessToken !== 'string') {
                            logger.error('API-CLIENT', 'accessToken is not a string', {
                                accessTokenType: typeof accessToken,
                                isArray: Array.isArray(accessToken),
                                accessTokenValue: String(accessToken).substring(0, 100), // First 100 chars for debugging
                                responseKeys: Object.keys(response || {})
                            });
                            this.tokenCache.refreshPromise = null;
                            return null;
                        }
                    }
                    
                    // Validate access token is not empty
                    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
                        logger.error('API-CLIENT', 'Access token acquisition returned empty/invalid token', {
                            hasAccessToken: !!response.accessToken,
                            hasIdToken: !!response.idToken,
                            scope: scope,
                            tokenType: typeof accessToken,
                            account: account?.username,
                        expiresOn: response.expiresOn
                    });
                    // This will trigger the error handler which will try interactive consent
                    throw new Error('Access token is empty or invalid');
                }
                
                authTrace('getApiAccessToken acquireTokenSilent SUCCESS', { tokenLen: accessToken.length });
                this.tokenCache.token = accessToken;
                this.tokenCache.expiresAt = response.expiresOn ? new Date(response.expiresOn).getTime() : null;
                this.tokenCache.refreshPromise = null;
                return accessToken;
            } else {
                // No scope configured - cannot make API calls
                logger.error('API-CLIENT', 'No API scope configured - cannot acquire access token', {
                    hasWindowEnv: !!(window._env_),
                    hasProcessEnv: !!(process.env.REACT_APP_ENTRA_API_SCOPE),
                    windowScope: window._env_?.ENTRA_API_SCOPE,
                    processScope: process.env.REACT_APP_ENTRA_API_SCOPE
                });
                this.tokenCache.refreshPromise = null;
                return null;
            }
        } catch (error) {
            const errCode = error?.errorCode ?? error?.name;
            const errMsg = (error?.message || '').slice(0, 150);
            authTrace('getApiAccessToken acquireTokenSilent FAILED', {
                errorCode: errCode,
                message: errMsg,
                hasAccount: !!account,
                hasScope: !!scope,
                pathname: typeof window !== 'undefined' ? window.location.pathname : ''
            });

            this.tokenCache.refreshPromise = null;

            // Entra External ID: on any silent failure, use full-page redirect.
            if (account && scope) {
                authTrace('getApiAccessToken REDIRECT acquireTokenRedirect (navigating away)', { errorCode: errCode });
                try {
                    const redirectRequest = this.buildTokenRequest(scope, account, false);
                    await this.msalInstance.acquireTokenRedirect(redirectRequest);
                } catch (redirectErr) {
                    authTrace('getApiAccessToken acquireTokenRedirect threw', { err: redirectErr?.message });
                }
                return null;
            }
            if (!account && scope) {
                authTrace('getApiAccessToken REDIRECT authMethods.login (navigating away)', { errorCode: errCode });
                try {
                    const { authMethods } = await import('./msalConfig');
                    await authMethods.login(this.msalInstance, {});
                } catch (loginErr) {
                    authTrace('getApiAccessToken authMethods.login threw', { err: loginErr?.message });
                }
                return null;
            }
            
            // If we have a cached token, return it even if expired to avoid blocking
            if (this.tokenCache.token) {
                logger.debug('API-CLIENT', 'Token acquisition failed, returning cached token (may be expired)');
                return this.tokenCache.token;
            }
            
            // Check for fatal auth errors requiring hard reset (loop detection)
            try {
                const { trackFailure, isUserAccountDeleted } = await import('./authRecovery');
                const errorObj = {
                    name: error.name,
                    message: error.message || '',
                    errorMessage: error.errorMessage || error.message || '',
                    errorCode: error.errorCode || '',
                    errorDescription: error.errorDescription || '',
                    errorCodes: error.errorCodes || [],
                    suberror: error.suberror
                };
                
                if (trackFailure(errorObj)) {
                    // Hard reset required - only triggered for fatal errors (AADSTS500341/user_account_deleted)
                    const reason = isUserAccountDeleted(errorObj) 
                        ? 'user_account_deleted' 
                        : 'fatal_error';
                    window.location.href = `/auth/reset?reason=${encodeURIComponent(reason)}`;
                    return null; // Stop processing
                }
            } catch (recoveryError) {
                // If recovery module fails to load, continue with normal error handling
                logger.warn('API-CLIENT', 'Failed to check auth recovery', { error: recoveryError.message });
            }
            
            // Handle authority mismatch - clear cache and require re-authentication
            if (error.name === 'ClientConfigurationError' && (error.errorCode === 'authority_mismatch' || error.message?.includes('authority_mismatch'))) {
                logger.warn('API-CLIENT', 'Authority mismatch detected - clearing MSAL cache');
                try {
                    this.msalInstance.clearCache();
                    // Clear localStorage MSAL entries
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith('msal.')) {
                            localStorage.removeItem(key);
                        }
                    });
                    // Clear sessionStorage MSAL entries
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('msal.')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                    logger.warn('API-CLIENT', 'MSAL cache cleared - user needs to log in again with correct authority');
                } catch (clearError) {
                    logger.error('API-CLIENT', 'Failed to clear MSAL cache', { error: clearError.message });
                }
                return null; // User needs to re-authenticate
            }
            
            // Handle InteractionRequiredAuthError - user needs to re-authenticate or consent
            // CRITICAL: For no_tokens_found errors, auto-logout instead of redirecting to Entra
            const isNoTokensFound = error.errorCode === 'no_tokens_found' || 
                                    error.message?.includes('no_tokens_found') ||
                                    error.message?.includes('No refresh token found in the cache');
            
            if (isNoTokensFound) {
                logger.error('API-CLIENT', 'No tokens found - performing auto-logout instead of redirecting to Entra', {
                    errorCode: error.errorCode,
                    errorMessage: error.message,
                    account: account?.username
                });
                
                // Import and call performAutoLogout
                try {
                    const { performAutoLogout } = await import('./authRecovery');
                    performAutoLogout('no_tokens_found');
                } catch (importError) {
                    logger.error('API-CLIENT', 'Failed to import performAutoLogout', { error: importError.message });
                    // Fallback: clear storage and redirect
                    try {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    } catch (e) {
                        logger.error('API-CLIENT', 'Fallback logout failed', { error: e.message });
                    }
                }
                return null;
            }
            
            // Handle other InteractionRequiredAuthError cases
            // CRITICAL: Don't force consent if refresh token expired - just re-authenticate
            if (error.name === 'InteractionRequiredAuthError' || error.errorCode === 'interaction_required') {
                const isRefreshTokenExpired = error.errorCode === 'refresh_token_expired' || 
                                               error.message?.includes('refresh_token_expired') ||
                                               error.message?.includes('Refresh token has expired');
                
                // If refresh token expired, user just needs to re-authenticate (not consent again)
                // Only force consent if we're sure the user hasn't consented to the API scope
                const needsConsent = !isRefreshTokenExpired && (
                    error.errorCode === 'consent_required' ||
                    error.message?.includes('consent_required') ||
                    error.message?.includes('AADSTS65001') // User consent required error code
                );
                
                logger.warn('API-CLIENT', 'Interaction required for API scope', {
                    scope: scope,
                    errorCode: error.errorCode,
                    errorMessage: error.message,
                    isRefreshTokenExpired,
                    needsConsent,
                    action: isRefreshTokenExpired ? 're-authenticate' : (needsConsent ? 'consent' : 'interactive login')
                });
                
                // CRITICAL: For InteractionRequiredAuthError (not no_tokens_found), also auto-logout
                // User requested: "dont send him to the entra url but just log out and homepage"
                logger.error('API-CLIENT', 'Interaction required - performing auto-logout instead of redirecting to Entra', {
                    errorCode: error.errorCode,
                    errorMessage: error.message,
                    account: account?.username
                });
                
                try {
                    const { performAutoLogout } = await import('./authRecovery');
                    performAutoLogout('interaction_required');
                } catch (importError) {
                    logger.error('API-CLIENT', 'Failed to import performAutoLogout', { error: importError.message });
                    // Fallback: clear storage and redirect
                    try {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    } catch (e) {
                        logger.error('API-CLIENT', 'Fallback logout failed', { error: e.message });
                    }
                }
                
                // Return null - NO automatic redirects
                return null;
            }
            
            // For other errors, we cannot use ID token fallback for API calls
            // API endpoints require access tokens, not ID tokens
            logger.error('API-CLIENT', 'Token acquisition failed - cannot use ID token for API calls', {
                errorName: error.name,
                errorMessage: error.message,
                errorCode: error.errorCode,
                scope: scope
            });
            
            return null; // Must return null - API calls require access tokens
        }
        })();
        
        // Wait for the refresh promise and return the token
        try {
            const token = await this.tokenCache.refreshPromise;
            return token;
        } catch (err) {
            // Error already handled in promise, return null
            return null;
        }
        } finally {
            this._getApiAccessTokenDepth = Math.max(0, (this._getApiAccessTokenDepth || 1) - 1);
        }
    }
    
    /**
     * Background token refresh - refreshes token proactively before expiration
     * Called automatically when token is close to expiring
     */
    _refreshTokenInBackground() {
        // Don't refresh if already refreshing or in cooldown
        if (this.tokenCache.refreshPromise) return;
        if (this.tokenCache.lastRefreshAttempt && 
            (Date.now() - this.tokenCache.lastRefreshAttempt) < this.TOKEN_REFRESH_COOLDOWN_MS) {
            return;
        }
        
        // Trigger refresh in background (fire and forget)
        this.getApiAccessToken().catch(err => {
            logger.debug('API-CLIENT', 'Background token refresh failed', { error: err.message });
        });
    }

    /**
     * Check if authentication is ready
     * @returns {boolean} True if authentication is ready
     */
    isAuthenticationReady() {
        try {
            if (!this.msalInstance) {
                return false;
            }
            
            // Check both getActiveAccount and getAllAccounts for robustness
            const activeAccount = this.msalInstance.getActiveAccount();
            if (activeAccount) {
                return true;
            }
            
            const accounts = this.msalInstance.getAllAccounts();
            const isReady = accounts && accounts.length > 0;
            
            return isReady;
        } catch (error) {
            // Silent fail - not critical
            return false;
        }
    }

    /**
     * Check if API client is ready for making authenticated requests
     * @returns {boolean} True if ready
     */
    isReady() {
        return this.config !== null && this.isAuthenticationReady();
    }

    /**
     * Wait for authentication to complete (NON-BLOCKING - for diagnostics only)
     * 
     * NOTE: This function is kept for diagnostics purposes only.
     * API calls now use on-demand token acquisition via getApiAccessToken()
     * and do NOT call this function, eliminating the 15-second blocking issue.
     * 
     * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 5000)
     * @returns {Promise<boolean>} True if authentication is ready
     */
    async waitForAuthentication(maxWaitTime = 5000) {
        const waitStart = performance.now();
        
        // If already ready, return immediately (most common case - no polling needed)
        if (this.authReady) {
            return true;
        }
        
        // BEST PRACTICE: Wait for MSAL instance to be initialized first
        // This prevents unnecessary polling when MSAL is null
        if (!this.msalInstance) {
            logger.debug('AUTH', 'MSAL instance not initialized, waiting for initialization');
            const msalReady = await this.waitForMsalInstance(2000); // Reduced wait time for diagnostics
            
            if (!msalReady) {
                logger.debug('AUTH', 'MSAL instance not initialized after wait - returning false for diagnostics');
                return false; // Fail fast
            }
        }
        
        // If authentication check is already in progress, return that shared promise
        if (this.authReadyPromise) {
            logger.debug('AUTH', 'Sharing existing auth wait promise');
            const result = await this.authReadyPromise;
            return result;
        }
        
        // Create shared promise: auth ready only when token is obtainable (avoids per-request 1.5s retry)
        this.authReadyPromise = (async () => {
            const startTime = Date.now();
            const tokenReady = async () => {
                const token = await this.getApiAccessToken();
                return token && typeof token === 'string' && token.trim().length > 0;
            };

            // Check immediately: accounts + one token acquisition
            if (this.isAuthenticationReady()) {
                if (await tokenReady()) {
                    this.authReady = true;
                    return true;
                }
            }

            let checkInterval = 100;
            while (Date.now() - startTime < maxWaitTime) {
                if (this.isAuthenticationReady()) {
                    if (await tokenReady()) {
                        this.authReady = true;
                        const totalWait = Date.now() - startTime;
                        if (totalWait > 500) {
                            logger.debug('AUTH', 'Auth ready after token acquisition (diagnostics)', { duration_ms: totalWait });
                        }
                        return true;
                    }
                }

                const elapsed = Date.now() - startTime;
                if (elapsed > 2000) {
                    checkInterval = 200;
                }
                const intervalMs = checkInterval;
                await new Promise(resolve => setTimeout(resolve, intervalMs));
            }

            const totalWait = Date.now() - startTime;
            logger.debug('AUTH', 'Authentication wait timeout (diagnostics)', {
                duration_ms: totalWait,
                maxWait_ms: maxWaitTime,
                hasMsalInstance: !!this.msalInstance,
                isReady: this.isAuthenticationReady()
            });
            return false;
        })();
        
        // Wait for the shared promise to complete
        const result = await this.authReadyPromise;
        
        // Clear the promise after completion
        this.authReadyPromise = null;
        
        const waitTime = performance.now() - waitStart;
        if (waitTime > 100) {
            logger.debug('AUTH', 'waitForAuthentication (diagnostics)', { duration_ms: waitTime });
        }
        
        return result;
    }

    /**
     * Append request name to URL as query parameter
     * @param {string} url - Original URL
     * @param {string} requestName - Request name to append
     * @returns {string} URL with requestName query parameter
     */
    _appendRequestName(url, requestName) {
        if (!requestName) {
            return url;
        }
        
        try {
            const urlObj = new URL(url);
            urlObj.searchParams.set('requestName', requestName);
            return urlObj.toString();
        } catch (e) {
            // If URL parsing fails (relative URL), append manually
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}requestName=${encodeURIComponent(requestName)}`;
        }
    }

    /**
     * Normalize URL for in-flight deduplication by stripping requestName so same logical request coalesces.
     * @param {string} url - Full URL (may include requestName query param)
     * @returns {string} URL without requestName param
     */
    _normalizeUrlForDedup(url) {
        if (!url || typeof url !== 'string') return url;
        try {
            const base = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'http://localhost';
            const urlObj = new URL(url, base);
            urlObj.searchParams.delete('requestName');
            return urlObj.toString();
        } catch (e) {
            return url;
        }
    }

    /**
     * Make authenticated API request (with centralized progressive backoff)
     * 
     * ALL API CALLS GO THROUGH THIS METHOD → Centralized Service → Progressive Backoff
     * 
     * This method:
     * 1. Handles authentication (MSAL token acquisition)
     * 2. Creates authenticated fetch function
     * 3. Wraps it with centralized service for:
     *    - Progressive exponential backoff (prevents request storms)
     *    - Circuit breaker pattern (stops after 5 failures)
     *    - Request deduplication (prevents duplicate calls)
     *    - Graceful error handling (container-missing errors handled)
     *    - Retry logic (automatic retries for retryable errors)
     * 
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {object} options - Request options
     * @param {string} options.requestName - Optional request name for DevTools (e.g., 'b-esper_ah_get_listtenants')
     * @returns {Promise<object>} API response
     */
    async apiRequest(endpoint, options = {}) {
        // Ensure configuration is loaded before making any requests
        const initStart = performance.now();
        await this.ensureInitialized();
        const initTime = performance.now() - initStart;
        if (initTime > 10) {
            logger.time('API', 'ensureInitialized', initStart, { endpoint: endpoint.substring(0, 50) });
        }

        // Check if this is a public endpoint that doesn't require authentication
        // Do this early to avoid waiting for auth unnecessarily
        const publicEndpoints = [
            '/v1/payments/products',
            '/api/v1/payments/products',
            '/api/v1/products',
            '/api/v1/marketplace'  // providers and products catalog (resale) — visible on public portal without login
        ];
        const isPublicEndpoint = publicEndpoints.some(publicPath =>
            endpoint.includes(publicPath)
        );

        // Protected endpoints: require ENTRA_API_SCOPE so we can acquire a Bearer token for the gateway
        if (!isPublicEndpoint) {
            const scope = (window._env_ && window._env_.ENTRA_API_SCOPE) || (process.env.REACT_APP_ENTRA_API_SCOPE);
            if (!scope || typeof scope !== 'string' || scope.trim().length === 0) {
                logger.error('API-CLIENT', 'ENTRA_API_SCOPE not configured; cannot acquire token for protected requests. Set in env-config.js / ConfigMap (website users).', {
                    hasWindowEnv: !!(window._env_),
                    endpoint: endpoint.substring(0, 60)
                });
                return {
                    success: false,
                    error: getApiUserMessage('authNotConfigured'),
                    requiresAuth: true
                };
            }
            await this.waitForAuthentication();
        }

        // Extract timeout FIRST - before destructuring to ensure it's available
        const timeout = (options && options.timeout !== undefined) ? options.timeout : (this.config?.timeout || 30000);
        
        const {
            method = 'GET',
            body = null,
            headers = {},
            // eslint-disable-next-line no-unused-vars
            retries = this.config.retryAttempts,
            requestName = null,
            skipPreferencesCache = false,
        } = options || {};
        
        // Log timeout for debugging cost pool creation
        if (endpoint.includes('cost-pools') && method === 'POST') {
            logger.debug('API-CLIENT', 'Cost pool creation request timeout', { timeout, optionsTimeout: options?.timeout, configTimeout: this.config?.timeout });
        }

        // Allow absolute endpoints to bypass baseUrl concatenation
        const isAbsolute = /^https?:\/\//i.test(endpoint);
        
        // Determine if baseUrl is localhost or APIM
        const isLocalhost = this.config.baseUrl && (this.config.baseUrl.includes('localhost') || this.config.baseUrl.includes('127.0.0.1'));
        const isApim = this.config.baseUrl && this.config.baseUrl.includes('azure-api.net');
        const baseUrlHasApi = this.config.baseUrl && this.config.baseUrl.includes('/api');
        
        // Add /api/ prefix logic:
        // - For localhost: always add /api/ to endpoint (baseUrl doesn't have it)
        // - For APIM: baseUrl should already have /api from getApiEndpoint(), so endpoint should NOT have /api/
        // - If APIM baseUrl is missing /api, add it to baseUrl itself (fix the baseUrl)
        let finalEndpoint = endpoint;
        let finalBaseUrl = this.config.baseUrl;
        
        if (!isAbsolute && this.config.baseUrl && !endpoint.startsWith('/api/')) {
            if (isLocalhost && !baseUrlHasApi) {
                // Localhost: add /api/ prefix to endpoint
                finalEndpoint = `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
            } else if (isApim && !baseUrlHasApi) {
                // APIM but baseUrl missing /api - fix the baseUrl itself
                logger.warn('API-CLIENT', 'APIM baseUrl missing /api prefix, fixing baseUrl', { baseUrl: this.config.baseUrl });
                finalBaseUrl = `${this.config.baseUrl}/api`;
            }
            // If baseUrl already has /api, endpoint should NOT have /api/ (already handled by !endpoint.startsWith('/api/'))
        }

        // Avoid relative URL when baseUrl is missing on localhost (request would hit React dev server and return HTML)
        if (!finalBaseUrl && typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
            finalBaseUrl = 'http://localhost:5100';
            if (!endpoint.startsWith('/api/')) {
                finalEndpoint = `/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
            }
        }
        
        let url = isAbsolute ? endpoint : `${finalBaseUrl}${finalEndpoint}`;
        
        // Final safety check: if URL is APIM and missing /api, add it
        // Check for /api as a path segment (not just "api" in domain)
        const hasApiPath = url && (url.endsWith('/api') || url.match(/\/api\/|\/api$/));
        if (!isAbsolute && url.includes('azure-api.net') && !hasApiPath) {
            // Insert /api after the domain
            url = url.replace('azure-api.net', 'azure-api.net/api');
            logger.warn('API-CLIENT', 'Final URL missing /api, fixed', { url });
        }
        
        // Append request name to URL if provided
        if (requestName) {
            url = this._appendRequestName(url, requestName);
        }
        
        // Update isPublicEndpoint check to include the final URL
        const isPublicEndpointFinal = isPublicEndpoint || publicEndpoints.some(publicPath => 
            url.includes(publicPath)
        );
        
        // Acquire token consistently using configured API scope; fallback to ID token
        let token = null;
        if (!isPublicEndpointFinal) {
            // Only require token for non-public endpoints
            try {
                token = await this.getApiAccessToken();
                
                // Strict validation: token must be a non-empty string
                if (!token || typeof token !== 'string' || token.trim().length === 0) {
                    logger.warn('API-CLIENT', 'No valid token available - authentication may be required', {
                        tokenType: typeof token,
                        tokenLength: token ? token.length : 0
                    });
                    // Grace period: right after auth callback, MSAL may not have token ready yet; retry once before logout
                    // Do NOT remove key before retry - concurrent requests must all see it and wait, so only one triggers logout
                    const AUTH_CALLBACK_JUST_COMPLETED_KEY = 'auth.callback.just_completed';
                    const runGraceRetry = async () => {
                        await new Promise(r => setTimeout(r, 800));
                        const retryToken = await this.getApiAccessToken();
                        if (retryToken && typeof retryToken === 'string' && retryToken.trim().length > 0) {
                            token = retryToken;
                            logger.info('API-CLIENT', 'Got token after callback grace period retry');
                        }
                        try { sessionStorage.removeItem(AUTH_CALLBACK_JUST_COMPLETED_KEY); } catch (_) {}
                    };
                    try {
                        const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(AUTH_CALLBACK_JUST_COMPLETED_KEY) : null;
                        if (raw) {
                            const ts = parseInt(raw, 10);
                            if (Number.isFinite(ts) && (Date.now() - ts) < 5000) {
                                await runGraceRetry();
                            }
                        } else if (
                            typeof window !== 'undefined' &&
                            window.location.pathname === '/' &&
                            this.msalInstance &&
                            this.msalInstance.getAllAccounts?.()?.length > 0
                        ) {
                            // Fallback: on home with accounts but no token (post-callback race) - retry once even without key
                            logger.debug('API-CLIENT', 'No grace key; on / with accounts - doing single retry before logout');
                            await runGraceRetry();
                        }
                    } catch (graceErr) {
                        // Ignore
                    }
                    if (!token || typeof token !== 'string' || token.trim().length === 0) {
                        const retryToken = await this._retryTokenOnceIfAccountExists();
                        if (retryToken) {
                            token = retryToken;
                        } else {
                            this._redirectToEntraLoginIfAccountExists();
                            return {
                                success: false,
                                error: getApiUserMessage('authRequired'),
                                requiresAuth: true
                            };
                        }
                    }
                }
            } catch (e) {
                logger.error('API-CLIENT', 'Failed to acquire authentication token', { error: e.message });
                // Grace period (same as main path): retry after 800ms; fallback if on / with accounts
                try {
                    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth.callback.just_completed') : null;
                    const doRetry = raw
                        ? (Number.isFinite(parseInt(raw, 10)) && (Date.now() - parseInt(raw, 10)) < 5000)
                        : (typeof window !== 'undefined' && window.location.pathname === '/' && this.msalInstance?.getAllAccounts?.()?.length > 0);
                    if (doRetry) {
                        await new Promise(r => setTimeout(r, 800));
                        token = await this.getApiAccessToken();
                        if (token && typeof token === 'string' && token.trim().length > 0) {
                            logger.info('API-CLIENT', 'Got token after callback grace period retry (catch path)');
                        }
                        try { sessionStorage.removeItem('auth.callback.just_completed'); } catch (_) {}
                    }
                } catch (graceErr) {
                    // Ignore
                }
                if (!token || typeof token !== 'string' || token.trim().length === 0) {
                    const retryToken = await this._retryTokenOnceIfAccountExists();
                    if (retryToken) {
                        token = retryToken;
                    } else {
                        this._redirectToEntraLoginIfAccountExists();
                        return {
                            success: false,
                            error: getApiUserMessage('authRequired'),
                            requiresAuth: true
                        };
                    }
                }
            }
        } else {
            // For public endpoints, try to get token if available, but don't fail if not
            // Use a more defensive approach to handle any errors during token acquisition
            try {
                // Only attempt token acquisition if MSAL is initialized
                if (this.msalInstance && this.initialized) {
                    token = await this.getApiAccessToken();
                    // If getApiAccessToken returns null (no account), that's fine for public endpoints
                    if (!token) {
                        token = null;
                    }
                } else {
                    // MSAL not initialized - that's fine for public endpoints
                    token = null;
                }
            } catch (e) {
                // Ignore ALL token errors for public endpoints - they don't require auth
                logger.debug('API-CLIENT', 'Token acquisition failed for public endpoint (expected if not authenticated)', {
                    endpoint: endpoint.substring(0, 50),
                    error: e.message
                });
                token = null;
            }
        }

        // WebSocket is connected in background (TenantContext/App). Do not block requests on connectWs();
        // first requests go over HTTP immediately; later requests use WS when isWsConnected() is true.

        // Double-check token is valid before proceeding (skip for public endpoints)
        if (!isPublicEndpointFinal && (!token || typeof token !== 'string' || token.trim().length === 0)) {
            logger.error('API-CLIENT', 'Token validation failed - refusing to make request without valid token');
            // Grace period (same as main path): retry after 800ms; fallback if on / with accounts
            try {
                const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth.callback.just_completed') : null;
                const doRetry = raw
                    ? (Number.isFinite(parseInt(raw, 10)) && (Date.now() - parseInt(raw, 10)) < 5000)
                    : (typeof window !== 'undefined' && window.location.pathname === '/' && this.msalInstance?.getAllAccounts?.()?.length > 0);
                if (doRetry) {
                    await new Promise(r => setTimeout(r, 800));
                    token = await this.getApiAccessToken();
                    if (token && typeof token === 'string' && token.trim().length > 0) {
                        logger.info('API-CLIENT', 'Got token after callback grace period retry (double-check path)');
                    }
                    try { sessionStorage.removeItem('auth.callback.just_completed'); } catch (_) {}
                }
            } catch (graceErr) {
                // Ignore
            }
            if (!token || typeof token !== 'string' || token.trim().length === 0) {
                const retryToken = await this._retryTokenOnceIfAccountExists();
                if (retryToken) {
                    token = retryToken;
                } else {
                    this._redirectToEntraLoginIfAccountExists();
                    return {
                        success: false,
                        error: getApiUserMessage('authRequired'),
                        requiresAuth: true
                    };
                }
            }
        }

        // Create internal fetch function that will be wrapped by centralized service
        const executeFetch = async () => {
            if (
                method === 'GET' &&
                !skipPreferencesCache &&
                typeof url === 'string' &&
                url.includes('/preferences/settings') &&
                !url.includes('/preferences/notifications')
            ) {
                const cached = this._getCached('preferences_settings');
                if (cached) {
                    return cached;
                }
            }

            // Guard: never send protected request without Authorization (prevents 403 from gateway)
            if (!isPublicEndpointFinal && (!token || typeof token !== 'string' || token.trim().length === 0)) {
                logger.warn('API-CLIENT', 'Refusing to send protected request without token', { endpoint: endpoint.substring(0, 60) });
                return {
                    success: false,
                    error: getApiUserMessage('authRequired'),
                    requiresAuth: true
                };
            }
            const executeFetchStartTime = performance.now();
            const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Prefer WebSocket for JSON requests when connected (FormData and streams stay on HTTP)
            const bodyIsFormData = body != null && typeof body === 'object' && typeof FormData !== 'undefined' && body instanceof FormData;
            const isStreamOrSse = (endpoint && (String(endpoint).includes('stream') || String(endpoint).includes('events') || String(endpoint).includes('sse'))) || (url && (String(url).includes('stream') || String(url).includes('events') || String(url).includes('sse')));
            const pathForWs = (() => {
                if (url.startsWith('http://') || url.startsWith('https://')) {
                    try {
                        const parsed = new URL(url);
                        return parsed.pathname + (parsed.search || '');
                    } catch (_) {
                        return url;
                    }
                }
                return url.startsWith('/') ? url : '/' + url;
            })();
            const isGatewayOnlyPath = pathForWs.startsWith('/api/me/');
            // Platform-public (no msalInstance): skip WebSocket path; use HTTP only
            if (this.msalInstance && !bodyIsFormData && !isStreamOrSse && !isGatewayOnlyPath) {
                try {
                    const ws = await import('./wsClient');
                    if (ws.isWsConnected()) {
                        const path = pathForWs;
                        const impersonation = getActiveImpersonation();
                        const wsHeaders = impersonation
                            ? {
                                'X-Impersonate-User-Id': impersonation.userId,
                                'X-Impersonate-Tenant-Id': impersonation.tenantId,
                                'X-Impersonate-Request-Id': impersonation.requestId,
                            }
                            : null;
                        const result = await ws.wsCall(method, path, body ?? null, wsHeaders);
                        if (
                            method === 'GET' &&
                            !skipPreferencesCache &&
                            path.includes('/preferences/settings') &&
                            result &&
                            result.success
                        ) {
                            this._setCached('preferences_settings', result, 30000);
                        }
                        return result;
                    }
                } catch (wsErr) {
                    if (wsErr && (wsErr.status != null || (wsErr.response && wsErr.response.status != null))) {
                        throw wsErr;
                    }
                    const isMutation = method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
                    if (isMutation) {
                        logger.warn('API-CLIENT', 'WebSocket mutation failed with connection error — not retrying via HTTP to avoid duplicate writes', { method, error: wsErr?.message });
                        throw wsErr;
                    }
                    logger.debug('API-CLIENT', 'WebSocket GET failed, falling back to HTTP', { error: wsErr?.message });
                }
            }

            const requestHeaders = {
                'X-Request-ID': requestId,
                ...(options.requestName && { 'X-Besper-Request-Name': options.requestName }),
                ...headers
            };
            const serialized = buildFetchBodyAndContentType(body);
            if (!getContentTypeFromHeaders(headers)) {
              if (body == null || body === undefined) {
                requestHeaders['Content-Type'] = 'application/json';
              } else if (!serialized.omitContentType && serialized.contentType) {
                requestHeaders['Content-Type'] = serialized.contentType;
              }
            }
            
            // Add Authorization header only if token is available
            if (token) {
                requestHeaders['Authorization'] = `Bearer ${token}`;
            }
            const impersonation = getActiveImpersonation();
            if (impersonation) {
                requestHeaders['X-Impersonate-User-Id'] = impersonation.userId;
                requestHeaders['X-Impersonate-Tenant-Id'] = impersonation.tenantId;
                requestHeaders['X-Impersonate-Request-Id'] = impersonation.requestId;
            }
            // Determine cache strategy based on request method and endpoint
            // GET requests to list endpoints should use HTTP cache
            let cacheOption = 'default';  // Default: respect Cache-Control headers from server
            if (method === 'GET') {
                // List endpoints can use HTTP cache for faster loads
                const listEndpoints = ['/resources', '/tenants', '/users', '/cost-pools', '/workspaces'];
                if (listEndpoints.some(e => endpoint.includes(e))) {
                    cacheOption = 'default';  // Use HTTP cache (browser will respect Cache-Control from server)
                }
            } else {
                // POST/PUT/DELETE: no cache
                cacheOption = 'no-cache';
            }

            const requestOptions = {
                method,
                headers: requestHeaders,
                cache: cacheOption,
                ...(body != null && { body: serialized.fetchBody })
            };

            // Final guard: never send protected /api/v1/ request without Authorization (prevents 403 from gateway)
            const hasAuthHeader = requestHeaders['Authorization'] && String(requestHeaders['Authorization']).trim().length > 0;
            if (!isPublicEndpointFinal && !hasAuthHeader) {
                logger.warn('API-CLIENT', 'Refusing to send protected request without Authorization header', { endpoint: endpoint.substring(0, 60), url: url.substring(0, 80) });
                return {
                    success: false,
                    error: getApiUserMessage('authRequired'),
                    requiresAuth: true
                };
            }

            // Track inflight requests for UI loading indicators
            updateInflight(+1);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            try {
                const fetchCallTime = performance.now();
                // Check for CORS requests (any external domain, not just specific ones)
                const isCors = url.includes('azurewebsites.net') || url.includes('azure-api.net') || (!url.includes('localhost') && !url.includes('127.0.0.1'));
                if (isCors) {
                    logger.debug('API', 'CORS request detected', { method, url: url.substring(0, 60) });
                }
                
                // Determine priority from endpoint
                const priority = options.priority !== undefined 
                    ? options.priority 
                    : getPriorityFromEndpoint(endpoint);
                
                // Execute fetch through request queue to limit concurrent requests
                const response = await queueRequest(async () => {
                    return await fetch(url, {
                        ...requestOptions,
                        signal: controller.signal
                    });
                }, priority);
                
                const fetchTime = performance.now() - fetchCallTime;
                const slowWarnMs = getSlowFetchWarnThresholdMs(url);
                if (fetchTime > slowWarnMs) {
                    logger.warn('API', 'Slow fetch response', {
                        method,
                        url: url.substring(0, 80),
                        duration_ms: fetchTime.toFixed(2),
                    });
                }

                clearTimeout(timeoutId);

                // Check for 304 Not Modified (from HTTP cache)
                if (response.status === 304) {
                    // 304 means cached version is still valid - return cached data from service worker/cache
                    // For now, we'll treat 304 as success and let the browser handle it
                    // In the future, we could implement a Service Worker to handle this better
                    updateInflight(-1);
                    // Return a success response - browser cache will provide the data
                    return {
                        success: true,
                        cached: true,
                        status: 304
                    };
                }

                // Parse response (empty JSON bodies must not use response.json() — throws on 404, etc.)
                const data = await parseHttpJsonResponseBody(response);

                // CRITICAL: Check response body for success even if HTTP status is 500
                // Some backends return 500 status but success:true in body (false positive)
                const bodyIndicatesSuccess = data && data.success === true;
                
                if (response.ok || bodyIndicatesSuccess) {
                    // If body indicates success but status is 500, log warning but treat as success
                    if (bodyIndicatesSuccess && !response.ok) {
                        logger.warn('API-CLIENT', 'Response body indicates success but HTTP status is not OK', {
                            status: response.status,
                            endpoint: endpoint.substring(0, 100),
                            data: data
                        });
                    }
                    
                    // Even on success, check if response indicates Entra user doesn't exist
                    // CRITICAL: Never trigger logout for onboarding endpoints (user may not be in app DB yet)
                    if (data && !data.success && data.error && !this._isOnboardingEndpoint(endpoint) && this._isEntraUserNotFoundError(data.error)) {
                        logger.warn('API-CLIENT', 'Entra External ID user not found in successful response - user may have been deleted. Logging out');
                        // Trigger logout asynchronously (don't await to avoid blocking)
                        this._handleEntraUserNotFound().catch(err => {
                            logger.error('API-CLIENT', 'Error during auto-logout from response data', { error: err.message });
                        });
                    }

                    if (
                        bodyIndicatesSuccess &&
                        (method === 'PUT' || method === 'PATCH') &&
                        typeof url === 'string' &&
                        url.includes('/preferences/settings')
                    ) {
                        this.invalidatePreferencesSettingsCache();
                    }

                    if (
                        method === 'GET' &&
                        bodyIndicatesSuccess &&
                        !skipPreferencesCache &&
                        typeof url === 'string' &&
                        url.includes('/preferences/settings') &&
                        !url.includes('/preferences/notifications')
                    ) {
                        this._setCached('preferences_settings', data, 30000);
                    }

                    return data;
                } else {
                    const error = new Error(`API Error: ${response.status} ${response.statusText}`);
                    error.response = response;
                    error.data = data;
                    error.status = response.status;
                    error.requestId = requestId;

                    // Check if error indicates Entra user doesn't exist (for 400 errors)
                    // CRITICAL: Skip logout check if response indicates provisioning/tenant context error
                    // (indicated by debug field, auto_provisioning_error, or auto_provisioning_skipped fields)
                    const isProvisioningError = data && (
                        data.debug || 
                        data.auto_provisioning_error !== undefined || 
                        data.auto_provisioning_skipped !== undefined ||
                        (data.error && (
                            data.error.toLowerCase().includes('auto-provisioning') ||
                            data.error.toLowerCase().includes('tenant context') ||
                            data.error.toLowerCase().includes('accessible tenants')
                        ))
                    );
                    // CRITICAL: Never trigger logout for onboarding endpoints (400 "user not found" = app user not in DB yet)
                    const isOnboardingEndpoint = this._isOnboardingEndpoint(endpoint);
                    if (response.status === 400 && data && data.error && !isProvisioningError && !isOnboardingEndpoint && this._isEntraUserNotFoundError(data.error)) {
                        logger.warn('API-CLIENT', 'Entra External ID user not found in 400 error response - user may have been deleted. Logging out');
                        // Trigger logout asynchronously (don't await to avoid blocking)
                        this._handleEntraUserNotFound().catch(err => {
                            logger.error('API-CLIENT', 'Error during auto-logout from 400 error', { error: err.message });
                        });
                    } else if (isProvisioningError) {
                        logger.debug('API-CLIENT', '400 error is provisioning/tenant context related - skipping user-not-found check', {
                            hasDebug: !!data.debug,
                            hasAutoProvisioningError: data.auto_provisioning_error !== undefined,
                            hasAutoProvisioningSkipped: data.auto_provisioning_skipped !== undefined
                        });
                    }

                    // Handle 401 token expiration with automatic refresh
                    if (response.status === 401) {
                        const errorMessage =
                            data?.error ||
                            data?.detail ||
                            (typeof data?.message === 'string' ? data.message : '') ||
                            error.message ||
                            '';
                        const tokenError = {
                            name: 'TokenError',
                            message: errorMessage || error.message || ''
                        };
                        const shouldAttemptRefresh = !options._isRetry && this.shouldForceRefresh(tokenError);
                        
                        if (shouldAttemptRefresh) {
                            try {
                                // Attempt proactive refresh using configured scope first
                                const scope = (window._env_ && window._env_.ENTRA_API_SCOPE) || (process.env.REACT_APP_ENTRA_API_SCOPE);
                                if (scope) {
                                    await this.msalInstance.acquireTokenSilent({
                                        scopes: [scope],
                                        account: this.msalInstance.getActiveAccount(),
                                        forceRefresh: true
                                    });
                                } else {
                                    // BEST PRACTICE: Only request API scope, not OIDC scopes
                                    // OIDC scopes are already in ID token from login
                                    const apiScope = (window._env_ && window._env_.ENTRA_API_SCOPE) || (process.env.REACT_APP_ENTRA_API_SCOPE);
                                    if (apiScope) {
                                        await this.msalInstance.acquireTokenSilent({
                                            scopes: [apiScope], // Only API scope
                                            account: this.msalInstance.getActiveAccount(),
                                            forceRefresh: true
                                        });
                                    } else {
                                        logger.error('API-CLIENT', 'No API scope configured - cannot refresh token');
                                        throw new Error('No API scope configured');
                                    }
                                }
                                
                                // Retry with new token (single attempt guarded by _isRetry)
                                return await this.apiRequest(endpoint, { 
                                    ...options, 
                                    _isRetry: true
                                });
                            } catch (refreshError) {
                                logger.error('API-CLIENT', 'Token refresh failed', { error: refreshError.message });
                                // If refresh fails, redirect to login using authMethods to ensure manual redirect
                                if (refreshError.name === 'InteractionRequiredAuthError') {
                                    const { authMethods } = await import('./msalConfig');
                                    await authMethods.login(this.msalInstance);
                                }
                                throw error;  // Throw original error
                            }
                        }
                    }
                    
                    // Handle 403 legal agreements required - server-side enforcement
                    // This ensures even if client-side cache is tampered with, server enforcement kicks in
                    if (response.status === 403 && data && data.error_code === 'legal_agreements_required') {
                        // Import getProfilePageUrl dynamically to avoid circular dependencies
                        const { getProfilePageUrl } = await import('./legalAgreementsGuard');
                        const profileUrl = getProfilePageUrl();
                        
                        // Check if we're already on the profile page - don't redirect if we are
                        const isOnProfilePage = window.location.pathname === profileUrl || 
                                              window.location.pathname.startsWith(profileUrl);
                        
                        // Check if onboarding overlay is visible - don't redirect during onboarding
                        // Check multiple ways to ensure we catch it:
                        // 1. Check for visible overlay
                        // 2. Check for overlay in DOM (might be transitioning)
                        // 3. Check if we're on a route that typically shows onboarding
                        const onboardingOverlay = document.querySelector('.onboarding-overlay.visible') || 
                                                  document.querySelector('.onboarding-overlay');
                        const isOnboardingRoute = window.location.pathname === '/profile' || 
                                                  window.location.pathname === '/' ||
                                                  window.location.search.includes('onboarding');
                        
                        // CRITICAL: Don't redirect if user is on a legal page - they need to read the agreements first
                        const isOnLegalPage = isLegalPage(window.location.pathname);
                        
                        // Don't redirect if:
                        // 1. We're already on the profile page
                        // 2. Onboarding overlay is present
                        // 3. We're on an onboarding route
                        // 4. We're on a legal page (users need to read agreements first)
                        if (isOnProfilePage || onboardingOverlay || isOnboardingRoute || isOnLegalPage) {
                            if (isOnLegalPage) {
                                logger.debug('API-CLIENT', 'Server returned 403: Legal agreements required, but user is on legal page - allowing them to read agreements without redirect');
                            } else {
                                logger.debug('API-CLIENT', 'Server returned 403: Legal agreements required, but already on profile or onboarding is active - skipping redirect');
                            }
                            // Return error so caller knows the request failed, but don't redirect
                            throw error;
                        }
                        
                        logger.warn('API-CLIENT', 'Server returned 403: Legal agreements required - redirecting to profile');
                        // Use replace to avoid adding to history
                        window.location.replace(profileUrl);
                        
                        // Return error so caller knows the request failed
                        throw error;
                    }
                    
                    throw error;
                }
            } catch (fetchError) {
                clearTimeout(timeoutId);
                
                const executeFetchTime = performance.now() - executeFetchStartTime;
                const status = fetchError.status;
                const urlOrRequest = (typeof url !== 'undefined' ? url : '') || (fetchError?.response?.url || fetchError?.url || '');
                const endpointStr = typeof endpoint !== 'undefined' ? endpoint : '';
                const pathForTenantId = (urlOrRequest || endpointStr || '').toString().replace(/\?.*$/, '');
                const isTenantUpdateById = (method === 'PUT' || method === 'PATCH') && /\/v1\/tenants\/[^/]+$/.test(pathForTenantId);
                const hasTenantsOrOther = (urlOrRequest && (urlOrRequest.includes('/v1/tenants') || urlOrRequest.includes('/v1/resources') || urlOrRequest.includes('/v1/provision-featured-products') || urlOrRequest.includes('/v1/users/get-or-create-by-email'))) || (endpointStr && (endpointStr.includes('/v1/tenants') || endpointStr.includes('/v1/resources') || endpointStr.includes('/v1/provision-featured-products') || endpointStr.includes('/v1/users/get-or-create-by-email')));
                const isExpectedNewUser = (status === 403 || status === 400) && !isTenantUpdateById && hasTenantsOrOther;
                if (isExpectedNewUser) {
                    logger.info('API', 'Expected for new user (no tenant/user doc yet) - guiding to onboarding', {
                        method,
                        url: (urlOrRequest || url || '').toString().substring(0, 60),
                        status,
                        duration_ms: executeFetchTime.toFixed(2)
                    });
                } else {
                    const msg = String(fetchError.message || fetchError.error || '').toLowerCase();
                    const isRetryableNetwork = !status && (
                        msg.includes('failed to fetch') ||
                        msg.includes('networkerror') ||
                        msg.includes('err_network_changed') ||
                        msg.includes('request timeout') ||
                        msg.includes('aborted') ||
                        fetchError.name === 'AbortError' ||
                        fetchError.name === 'TypeError'
                    );
                    if (isRetryableNetwork) {
                        logger.debug('API', 'Network error (will retry)', {
                            method,
                            url: (urlOrRequest || url || '').toString().substring(0, 60),
                            error: fetchError.message,
                            duration_ms: executeFetchTime.toFixed(2)
                        });
                    } else {
                        logger.error('API', 'Fetch error', {
                            method,
                            url: (urlOrRequest || url || '').toString().substring(0, 60),
                            error: fetchError.message,
                            duration_ms: executeFetchTime.toFixed(2)
                        });
                    }
                }
                
                // Check if error indicates Entra user doesn't exist (before creating error object)
                // CRITICAL: Never trigger logout for onboarding endpoints
                const errorMsg = fetchError.message || fetchError.error || '';
                const isOnboardingRequest = this._isOnboardingEndpoint(urlOrRequest || '') || this._isOnboardingEndpoint(endpointStr || '');
                if (!isOnboardingRequest && this._isEntraUserNotFoundError(errorMsg)) {
                    logger.warn('API-CLIENT', 'Entra External ID user not found in API request error - user may have been deleted. Logging out');
                    // Trigger logout asynchronously (don't await to avoid blocking)
                    this._handleEntraUserNotFound().catch(err => {
                        logger.error('API-CLIENT', 'Error during auto-logout from apiRequest', { error: err.message });
                    });
                }
                
                // Create a new error object to avoid readonly property issues
                const apiError = new Error(fetchError.message || 'Request failed');
                apiError.name = fetchError.name || 'APIError';
                apiError.status = fetchError.status;
                apiError.response = fetchError.response;
                apiError.data = fetchError.data;
                apiError.requestId = requestId;

                if (fetchError.name === 'AbortError') {
                    apiError.message = 'Request timeout';
                }

                throw apiError;
            } finally {
                // Track inflight completion for UI loading indicators
                updateInflight(-1);
            }
        };

        // In-flight request deduplication: normalize URL (strip requestName) so same logical request coalesces
        const normalizedUrl = this._normalizeUrlForDedup(url);
        const cacheKey = `${method}:${normalizedUrl}:${JSON.stringify(body || {})}`;
        const apiRequestStartTime = performance.now();
        
        if (this.pendingRequests.has(cacheKey)) {
            logger.debug('API', 'Request deduplicated', { method, endpoint: endpoint.substring(0, 50) });
            return this.pendingRequests.get(cacheKey);
        }

        // Create promise for this request
        const requestPromise = this.apiService.executeWithBackoff(this, endpoint, {
            ...options,
            method: method, // Pass method explicitly for retry logic
            _executeFetch: executeFetch
        }).then(response => {
            logger.time('API', `${method} ${endpoint}`, apiRequestStartTime, {
                status: 'success',
                endpoint: typeof endpoint === 'string' ? endpoint.substring(0, 200) : '',
            });
            import('./wsClient')
                .then((m) => {
                    try {
                        if (typeof m.notifyApiGatewayReachable === 'function') m.notifyApiGatewayReachable();
                    } catch (_) {}
                })
                .catch(() => {});
            return response;
        }).catch(error => {
            const status = error?.status;
            const isTenantUpdateById = (method === 'PUT' || method === 'PATCH') && /\/v1\/tenants\/[^/]+(\?|$)/.test(endpoint.replace(/\?.*$/, ''));
            const isExpectedNewUser = (status === 403 || status === 400) && !isTenantUpdateById && (
                endpoint.includes('/v1/tenants') ||
                endpoint.includes('/v1/resources') ||
                endpoint.includes('/v1/provision-featured-products') ||
                endpoint.includes('/v1/users/get-or-create-by-email')
            );
            if (isExpectedNewUser) {
                logger.info('API', 'Expected for new user (no tenant/user doc yet) - guiding to onboarding', {
                    method,
                    endpoint: endpoint.substring(0, 50),
                    status,
                    duration_ms: (performance.now() - apiRequestStartTime).toFixed(2)
                });
            } else {
                logger.error('API', `${method} ${endpoint} failed`, { 
                    error: error.message,
                    duration_ms: (performance.now() - apiRequestStartTime).toFixed(2)
                });
            }
            throw error;
        }).finally(() => {
            // Remove from pending requests when done (success or failure)
            this.pendingRequests.delete(cacheKey);
        });

        // Store pending request
        this.pendingRequests.set(cacheKey, requestPromise);
        return requestPromise;
    }

    /**
     * Delay helper for retry logic
     * @param {number} ms - Milliseconds to delay
     * @returns {Promise} Promise that resolves after delay
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Memory cache helper methods for short-lived caching
     */
    _getCached(key) {
        const entry = this.memoryCache.get(key);
        if (entry && Date.now() < entry.expiresAt) {
            return entry.data;
        }
        this.memoryCache.delete(key);
        return null;
    }

    _setCached(key, data, ttl = this.CACHE_TTL_MS) {
        this.memoryCache.set(key, { data, expiresAt: Date.now() + ttl });
    }

    invalidatePreferencesSettingsCache() {
        this.memoryCache.delete('preferences_settings');
    }

    // =================================================================
    // DASHBOARD API METHODS
    // =================================================================


    // =================================================================
    // WORKSPACE API METHODS  
    // =================================================================

    /**
     * List user workspaces with hierarchy
     * @param {object} params - Query parameters
     * @returns {Promise<object>} Workspaces list with hierarchy
     */
    async listWorkspaces(params = {}) {
        const query = new URLSearchParams();
        if (params.includeStats !== undefined) {
            query.append('include_stats', params.includeStats);
        }
        if (params.parentId) {
            query.append('parent_id', params.parentId);
        }
        const endpoint = `/v1/tenants${query.toString() ? `?${query.toString()}` : ''}`;
        return this.apiRequest(endpoint, { 
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listtenants')
        });
    }

    /**
     * Get workspace details
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise<object>} Workspace details
     */
    async getWorkspace(workspaceId) {
        return this.apiRequest(`/v1/tenants/${workspaceId}`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'gettenant')
        });
    }

    /**
     * Get tenant details (alias for getWorkspace for consistency)
     * @param {string} tenantId - Tenant ID
     * @param {string} requestName - Optional request name for DevTools
     * @returns {Promise<object>} Tenant details
     */
    async getTenant(tenantId, requestName = null) {
        if (requestName) {
            return this.apiRequest(`/v1/tenants/${tenantId}`, {
                method: 'GET',
                requestName: requestName
            });
        }
        return this.getWorkspace(tenantId);
    }

    /**
     * Create new workspace
     * @param {object} workspaceData - Workspace data
     * @returns {Promise<object>} Created workspace
     */
    async createWorkspace(workspaceData) {
        return this.apiRequest('/v1/tenants', {
            method: 'POST',
            body: workspaceData,
            requestName: generateRequestName('ah', 'post', 'createtenant')
        });
    }

    /**
     * Update workspace
     * @param {string} workspaceId - Workspace ID
     * @param {object} updates - Updates to apply
     * @returns {Promise<object>} Updated workspace
     */
    async updateWorkspace(workspaceId, updates) {
        return this.apiRequest(`/v1/tenants/${workspaceId}`, {
            method: 'PUT',
            body: { update: updates },
            requestName: generateRequestName('ah', 'put', 'updatetenant')
        });
    }

    /**
     * Update tenant (alias for updateWorkspace for consistency)
     * @param {string} tenantId - Tenant ID
     * @param {object} updates - Updates to apply
     * @returns {Promise<object>} Updated tenant
     */
    async updateTenant(tenantId, updates) {
        return this.updateWorkspace(tenantId, updates);
    }

    /**
     * Delete workspace
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise<object>} Deletion result
     */
    async deleteWorkspace(workspaceId) {
        return this.apiRequest(`/v1/tenants/${workspaceId}`, {
            method: 'DELETE',
            requestName: generateRequestName('ah', 'delete', 'deletetenant')
        });
    }

    /**
     * Get workspace users
     * @param {string} workspaceId - Workspace ID
     * @returns {Promise<object>} Workspace users
     */
    async getWorkspaceUsers(workspaceId) {
        return this.apiRequest(`/v1/tenants/${workspaceId}/users`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listworkspaceusers')
        });
    }

    // =================================================================
    // USER API METHODS
    // =================================================================

    /**
     * List users
     * @param {object} params - Query parameters (scope: 'user' | 'tenant'; tenant_id: for scope=tenant)
     * @returns {Promise<object>} Users list
     */
    async listUsers(params = {}) {
        const query = new URLSearchParams();
        // scope=user: all users visible to current user (all accessible tenants)
        // scope=tenant: users in the given tenant (requires tenant_id) — e.g. for Cost Pool Admin dropdown
        const scope = params.scope || 'user';
        query.append('scope', scope);
        if (params.tenant_id) {
            query.append('tenant_id', params.tenant_id);
        }
        if (params.workspaceId) {
            query.append('workspace_id', params.workspaceId);
        }
        if (params.search) {
            query.append('search', params.search);
        }

        const endpoint = `/v1/users${query.toString() ? `?${query.toString()}` : ''}`;
        return this.apiRequest(endpoint, { 
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listusers')
        });
    }

    /**
     * Get user details
     * @param {string} userId - User ID
     * @returns {Promise<object>} User details
     */
    async getUser(userId) {
        return this.apiRequest(`/v1/users/${userId}`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getuser')
        });
    }

    /**
     * Invite user
     * @param {object} userData - User invitation data
     * @returns {Promise<object>} Invitation result
     */
    async inviteUser(userData) {
        return this.apiRequest('/v1/users', {
            method: 'POST',
            body: userData,
            requestName: generateRequestName('ah', 'post', 'createuser')
        });
    }

    /**
     * Update user
     * @param {string} userId - User ID
     * @param {object} updates - Updates to apply
     * @returns {Promise<object>} Updated user
     */
    async updateUser(userId, updates) {
        return this.apiRequest(`/v1/users/${userId}`, {
            method: 'PUT',
            body: updates,
            requestName: generateRequestName('ah', 'put', 'updateuser')
        });
    }

    /**
     * Delete user
     * @param {string} userId - User ID
     * @returns {Promise<object>} Deletion result
     */
    async deleteUser(userId) {
        return this.apiRequest(`/v1/users/${userId}`, {
            method: 'DELETE',
            requestName: generateRequestName('ah', 'delete', 'deleteuser')
        });
    }

    /**
     * Get user removal options (tenants where user can be removed)
     * @param {string} userId - User ID
     * @returns {Promise<object>} Removal options with tenant list
     */
    async getUserRemovalOptions(userId) {
        return this.apiRequest(`/v1/users/${userId}/removal-options`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getuserremovaloptions')
        });
    }

    /**
     * Remove user from tenants
     * @param {string} userId - User ID
     * @param {string[]} tenantIds - Array of tenant IDs to remove user from
     * @returns {Promise<object>} Removal result
     */
    async removeUserFromTenants(userId, tenantIds) {
        return this.apiRequest(`/v1/users/${userId}/remove-from-tenants`, {
            method: 'POST',
            body: { tenant_ids: tenantIds },
            requestName: generateRequestName('ah', 'post', 'removeuserfromtenants')
        });
    }

    /**
     * Get aggregated permissions and member groups for a user (role_management).
     * @param {string} userId - User ID (same as users collection _id)
     * @returns {Promise<object>} { success, user_id, permissions, accessible_tenants, main_tenant_id, per_tenant_permissions, member_groups }
     */
    async getPermissionsForUser(userId) {
        return this.apiRequest(`/v1/permissions/user/${encodeURIComponent(userId)}`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getpermissionsforuser')
        });
    }

    /**
     * List security groups for a tenant.
     * @param {string} tenantId - Tenant ID
     * @returns {Promise<object>} { success, data: [{ id, name, tenant_id, ... }] }
     */
    async listSecurityGroups(tenantId) {
        const query = new URLSearchParams({ tenant_id: tenantId });
        return this.apiRequest(`/v1/security-groups?${query}`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listsecuritygroups')
        });
    }

    /**
     * Add a user (or role) to a security group.
     * @param {string} groupId - Security group ID
     * @param {{ user_id?: string, role_id?: string }} body - user_id or role_id
     * @returns {Promise<object>} { success, modified }
     */
    async addGroupMember(groupId, body) {
        return this.apiRequest(`/v1/security-groups/${encodeURIComponent(groupId)}/members`, {
            method: 'POST',
            body,
            requestName: generateRequestName('ah', 'post', 'addgroupmember')
        });
    }

    /**
     * Remove a member (user or role) from a security group.
     * @param {string} groupId - Security group ID
     * @param {string} memberId - User ID or role ID to remove
     * @returns {Promise<object>} { success }
     */
    async removeGroupMember(groupId, memberId) {
        return this.apiRequest(`/v1/security-groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, {
            method: 'DELETE',
            requestName: generateRequestName('ah', 'delete', 'removegroupmember')
        });
    }

    /**
     * Delete user (legacy method - kept for compatibility)
     * @param {string} userId - User ID
     * @returns {Promise<object>} Deletion result
     */
    async deleteUserLegacy(userId) {
        return this.apiRequest(`/v1/users/${userId}`, {
            method: 'DELETE',
            requestName: generateRequestName('ah', 'delete', 'deleteuser')
        });
    }

    /**
     * Resolve display names from Mongo `users` for the given emails (account switcher). Authenticated POST.
     * @param {string[]} emails
     * @returns {Promise<{ success: boolean, by_email?: Record<string, { name: string, email: string }>, error?: string }>}
     */
    async resolveUsersDisplayByEmails(emails) {
        if (!Array.isArray(emails) || emails.length === 0) {
            return { success: true, by_email: {} };
        }
        const unique = [
            ...new Set(
                emails.filter(
                    (e) => typeof e === 'string' && e.includes('@') && e.trim().length >= 3,
                ),
            ),
        ].slice(0, 24);
        if (unique.length === 0) {
            return { success: true, by_email: {} };
        }
        return this.apiRequest('/v1/users/display-by-emails', {
            method: 'POST',
            body: { emails: unique },
            requestName: generateRequestName('ah', 'post', 'displaybyemails'),
        });
    }

    /**
     * Resolve display from Mongo `users` by Entra object id. Authenticated POST.
     * @param {string[]} oids
     * @returns {Promise<{ success: boolean, by_oid?: Record<string, { name: string, email: string }>, error?: string }>}
     */
    async resolveUsersDisplayByOids(oids) {
        if (!Array.isArray(oids) || oids.length === 0) {
            return { success: true, by_oid: {} };
        }
        const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        const unique = [
            ...new Set(
                oids
                    .filter((o) => typeof o === 'string' && o.trim().length > 0)
                    .map((o) => o.trim().toLowerCase())
                    .filter((o) => UUID_RE.test(o)),
            ),
        ].slice(0, 24);
        if (unique.length === 0) {
            return { success: true, by_oid: {} };
        }
        return this.apiRequest('/v1/users/display-by-oids', {
            method: 'POST',
            body: { oids: unique },
            requestName: generateRequestName('ah', 'post', 'displaybyoids'),
        });
    }

    /**
     * Get current user's profile
     * @param {{ includeTenantCompany?: boolean }} [options] — when true, request tenant company merge for legacy onboarding prefill (slower).
     * @returns {Promise<object>} User profile
     */
    async getUserProfile(options = {}) {
        const includeTenantCompany = options.includeTenantCompany === true;
        const PROFILE_CACHE_TTL_MS = 30000; // 30s to reduce duplicate calls from compliance + other consumers
        const cacheKey = includeTenantCompany ? 'user_profile_tenant_company' : 'user_profile';
        const cached = this._getCached(cacheKey);
        if (cached) return cached;

        const profilePath = includeTenantCompany
            ? '/v1/users/profile?include_tenant_company=1'
            : '/v1/users/profile';

        try {
            // Try the APIM endpoint
            const result = await this.apiRequest(profilePath, {
                method: 'GET',
                requestName: generateRequestName('ah', 'get', 'getprofile')
            });

            // Check if response indicates user doesn't exist in Entra External ID
            if (!result.success && result.error) {
                if (this._isEntraUserNotFoundError(result.error)) {
                    logger.warn('API-CLIENT', 'Entra External ID user not found in profile response - user may have been deleted. Logging out');
                    await this._handleEntraUserNotFound();
                    return result; // Return the error response but logout is triggered
                }
            }

            if (result && result.success) {
                const payload = result.data ?? result.user;
                const provisionalNoDoc = payload && payload._no_user_doc === true;
                if (!provisionalNoDoc) {
                    this._setCached(cacheKey, result, PROFILE_CACHE_TTL_MS);
                }
            }
            return result;
            
        } catch (error) {
            logger.error('API-CLIENT', 'APIM profile endpoint failed', { error: error.message });
            
            // Check if error indicates Entra user doesn't exist
            if (this._isEntraUserNotFoundError(error.message || error.error || '')) {
                logger.warn('API-CLIENT', 'Entra External ID user not found in profile error - user may have been deleted. Logging out');
                await this._handleEntraUserNotFound();
                // Return explicit error - no fallback
                return {
                    success: false,
                    error: 'User not found in Entra External ID',
                    data: null
                };
            }
            
            // Return explicit error - no fallback
            return {
                success: false,
                error: error.message || 'Failed to load user profile',
                data: null
            };
        }
    }

    /**
     * Get current user's activity rollup (by day, by method/object).
     * Uses gateway route GET /api/me/activity (same base URL and auth as other customer-portal calls).
     * @param {{ from?: string, to?: string }} params - Optional date range (YYYY-MM-DD)
     * @returns {Promise<{ from: string, to: string, series: Array, totals: object }|{ success: false, error: string }>}
     */
    async getMyActivity(params = {}) {
        const query = new URLSearchParams();
        if (params.from) query.set('from', params.from);
        if (params.to) query.set('to', params.to);
        const queryStr = query.toString();
        const endpoint = `/me/activity${queryStr ? `?${queryStr}` : ''}`;
        return this.apiRequest(endpoint, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'myactivity')
        });
    }

    /**
     * Recent system log rows for the current user (ClickHouse via gateway → data-engine).
     * @param {{ since?: string, until?: string, limit?: number, page?: number }} params
     */
    async getMyActivityEvents(params = {}) {
        const query = new URLSearchParams();
        if (params.since) query.set('since', params.since);
        if (params.until) query.set('until', params.until);
        if (params.limit != null) query.set('limit', String(params.limit));
        if (params.page != null) query.set('page', String(params.page));
        const queryStr = query.toString();
        const endpoint = `/v1/logs/system/me/events${queryStr ? `?${queryStr}` : ''}`;
        return this.apiRequest(endpoint, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'myactivityevents')
        });
    }

    /**
     * Get or create user by email using secure APIM JWT token extraction
     * This method leverages APIM's JWT token validation and email extraction
     * instead of sending email in the request payload for better security
     * @param {string} requestName - Optional request name for DevTools
     * @returns {Promise<object>} User data with legal agreements and tenant info
     */
    async getUserOrCreateByEmail(requestName = null) {
        logger.debug('API-CLIENT', 'Getting/creating user via secure APIM JWT token extraction');
        
        try {
            // Always attempt provisioning now that local bridging routes exist

            // Use the secure APIM endpoint that extracts email from JWT token
            // No need to send email in payload - APIM extracts it from the validated JWT
            // NOTE: apiRequest already adds /api prefix, so don't include it here
            const response = await this.apiRequest('/v1/users/get-or-create-by-email', {
                method: 'POST',
                body: {}, // Empty body - email extracted from JWT by APIM
                requestName: requestName || generateRequestName('ah', 'post', 'getorcreateuser')
            });
            
            // Check if response indicates user doesn't exist in Entra External ID
            if (!response.success && response.error) {
                if (this._isEntraUserNotFoundError(response.error)) {
                    logger.warn('API-CLIENT', 'Entra External ID user not found - user may have been deleted. Logging out');
                    // Trigger logout
                    await this._handleEntraUserNotFound();
                    return response; // Return the error response but logout is triggered
                }
            }
            
            // If user was auto-provisioned, detect and set initial browser preferences
            if (response.success && response.auto_provisioned) {
                try {
                    // Detect browser language and theme
                    const browserLang = navigator.language.split('-')[0];
                    const browserTheme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                    
                    // Call the detect-preferences endpoint
                    await this.apiRequest('/v1/users/detect-preferences', {
                        method: 'POST',
                        body: {
                            language: browserLang,
                            theme: browserTheme
                        },
                        requestName: generateRequestName('ah', 'post', 'detectpreferences')
                    });
                    
                    logger.info('API-CLIENT', 'Set initial preferences for auto-provisioned user', { language: browserLang, theme: browserTheme });
                } catch (prefError) {
                    // Non-critical - log but don't fail the user creation
                    logger.warn('API-CLIENT', 'Failed to set initial preferences for auto-provisioned user', { error: prefError.message });
                }
            }
            
            return response;
            
        } catch (error) {
            logger.error('API-CLIENT', 'Failed to get/create user via APIM JWT extraction', { error: error.message });
            
            // Check if error indicates Entra user doesn't exist
            if (this._isEntraUserNotFoundError(error.message || error.error || '')) {
                logger.warn('API-CLIENT', 'Entra External ID user not found in error - user may have been deleted. Logging out');
                // Trigger logout
                await this._handleEntraUserNotFound();
            }
            
            // Optionally record failure; no gating on this value
            try { sessionStorage.setItem('provisioning_api_failure', Date.now().toString()); } catch (_) {}
            throw error;
        }
    }

    /**
     * Check if endpoint/URL is an onboarding endpoint (where "user not found" means app user not in DB yet, not Entra deleted).
     * @param {string} endpointOrUrl - Endpoint path or full URL
     * @returns {boolean} True if this is an onboarding endpoint
     */
    _isOnboardingEndpoint(endpointOrUrl) {
        if (!endpointOrUrl || typeof endpointOrUrl !== 'string') return false;
        const s = endpointOrUrl.toLowerCase();
        return (
            s.includes('onboarding-legal-agreements') ||
            s.includes('onboarding-company-data') ||
            s.includes('onboarding-user-type') ||
            s.includes('onboarding-cost-pool-agreements') ||
            s.includes('validate-tax-id')
        );
    }

    /**
     * Check if error indicates Entra External ID user doesn't exist
     * @param {string} error - Error message or error object
     * @returns {boolean} True if error indicates Entra user doesn't exist
     */
    _isEntraUserNotFoundError(error) {
        if (!error) return false;
        
        const errorStr = String(error).toLowerCase();
        
        // CRITICAL: Only trigger logout for actual Entra user deletion errors, not provisioning/tenant context errors
        // Exclude ALL provisioning/tenant context errors FIRST - these should NEVER trigger logout
        const excludedPatterns = [
            'failed to resolve tenant context',
            'auto-provisioning failed',
            'auto-provisioning skipped',
            'auto-provisioning',
            'failed to create user',
            'tenant context',
            'no accessible tenants',
            'accessible tenants',
            'rate limited',
            'temporarily unavailable',
            'container missing',
            'permission denied',
            'authentication required',
            'token expired',
            'invalid token',
            'provisioning',
            'resolve tenant',
            'tenant resolution',
            'failed to create user'
        ];
        
        // CRITICAL: If error matches ANY excluded pattern, it's NOT an Entra user deletion error
        // Check this FIRST before any other logic
        if (excludedPatterns.some(pattern => errorStr.includes(pattern))) {
            logger.debug('API-CLIENT', 'Error excluded from user-not-found check (provisioning/tenant context error)', { 
                error: errorStr.substring(0, 100),
                matchedPattern: excludedPatterns.find(pattern => errorStr.includes(pattern))
            });
            return false;
        }
        
        // Check for patterns indicating Entra user doesn't exist (user was deleted from Entra)
        // Pattern 1: "User not found: email@domain.com" (with specific email)
        // Pattern 2: "user not found in entra" or "user not found in external id"
        // Pattern 3: "entra external id user not found" or "user does not exist in entra"
        const hasUserNotFound = errorStr.includes('user not found') || 
                               errorStr.includes('user does not exist') ||
                               errorStr.includes('user does not exist in');
        const hasEmailPattern = /@[a-z0-9.-]+\.[a-z]{2,}/i.test(errorStr);
        const hasEntraReference = (errorStr.includes('entra') || errorStr.includes('external id')) &&
                                 (errorStr.includes('not found') || errorStr.includes('does not exist'));
        
        // Must have "user not found" pattern AND either:
        // 1. Email pattern (specific user not found), OR
        // 2. Explicit Entra/External ID reference with "not found" (user deleted from Entra)
        const isUserNotFound = hasUserNotFound && (hasEmailPattern || hasEntraReference);
        
        if (isUserNotFound) {
            logger.warn('API-CLIENT', 'Potential Entra user-not-found error detected', { 
                error: errorStr.substring(0, 100),
                hasUserNotFound,
                hasEmailPattern,
                hasEntraReference
            });
        }
        
        return isUserNotFound;
    }

    /**
     * Handle case where Entra External ID user doesn't exist (user was deleted)
     * Automatically logs out the user and redirects to homepage
     */
    async _handleEntraUserNotFound() {
        // Prevent multiple simultaneous logout attempts
        if (this._logoutInProgress) {
            logger.debug('API-CLIENT', 'Logout already in progress, skipping');
            return;
        }
        
        this._logoutInProgress = true;
        try {
            logger.info('API-CLIENT', 'Auto-logout: Entra External ID user not found');
            
            // Use centralized auth recovery for consistent logout behavior
            const { performAutoLogout } = await import('./authRecovery');
            performAutoLogout('user_not_found');
            // performAutoLogout redirects to homepage - we won't reach here
            
        } catch (logoutError) {
            logger.error('API-CLIENT', 'Error during auto-logout', { error: logoutError.message });
            // Final fallback: clear everything and redirect to homepage
            try {
                localStorage.clear();
                sessionStorage.clear();
            } catch (clearError) {
                logger.warn('API-CLIENT', 'Error clearing storage in final fallback', { error: clearError.message });
            }
            // Redirect to homepage
            window.location.href = window.location.origin + '/';
        } finally {
            // Reset flag after a delay to allow redirect to happen
            setTimeout(() => {
                this._logoutInProgress = false;
            }, 5000);
        }
    }

    /**
     * Update current user's profile via backend API
     * @param {object} profileData - Profile data to update
     * @returns {Promise<object>} Updated profile
     */
    async updateUserProfile(profileData) {
        try {
            // Use the backend API endpoint for profile updates
            const response = await this.apiRequest('/v1/users/profile', {
                method: 'PUT',
                body: profileData,
                requestName: generateRequestName('ah', 'put', 'updateprofile')
            });
            
            return response;
        } catch (error) {
            logger.error('API-CLIENT', 'Profile update failed', { error: error.message });
            throw new Error(`Profile update failed: ${error.message}`);
        }
    }
    
    async resendUserLegalAgreements(language = null) {
        const endpoint = '/v1/users/resend-legal-agreements';
        const body = language ? { language } : {};
        
        try {
            const response = await this.post(endpoint, body);
            return response;
        } catch (error) {
            logger.error('API-CLIENT', 'Error resending legal agreements', { error: error.message });
            throw error;
        }
    }
    
    async updateUserLegalAgreements(legalAgreements) {
        
        // Helper to normalize agreement value to boolean
        // Handles: boolean, object with 'agreed' property, undefined, null
        const normalizeAgreement = (value) => {
            if (typeof value === 'boolean') {
                return value;
            }
            if (value && typeof value === 'object' && 'agreed' in value) {
                return value.agreed === true;
            }
            return false; // Default to false for undefined, null, or invalid types
        };
        
        // Map frontend format to API format
        // CRITICAL: Ensure all fields are present with explicit boolean values (not undefined)
        // Note: business_data_processing_agreement is not sent - we only use data_processing_agreement
        const apiLegalAgreements = {
            terms_of_service: normalizeAgreement(legalAgreements.termsOfService),
            data_processing_agreement: normalizeAgreement(legalAgreements.dataProcessingAgreement),
            privacy_policy: normalizeAgreement(legalAgreements.privacyPolicy),
            end_user_license_agreement: normalizeAgreement(legalAgreements.endUserLicenseAgreement),
            acceptable_use: normalizeAgreement(legalAgreements.acceptableUse)
        };
        
        // Get current language preference for PDF and email generation
        // Priority: 1) document.documentElement.lang (set by i18n), 2) localStorage, 3) browser default
        const currentLanguage = document.documentElement.lang || 
                               localStorage.getItem('besper-language') || 
                               navigator.language.split('-')[0] || 
                               'en';
        
        // Log the request for debugging
        logger.debug('API-CLIENT', 'Updating legal agreements', { language: currentLanguage });

        // Use apiRequest which properly handles authentication via JWT token
        // The API endpoint extracts email from the JWT token automatically (no need to send email)
        // Include language so PDFs and emails are generated in the user's current language preference
        const response = await this.apiRequest('/v1/users/update-legal-agreements', {
            method: 'POST',
            body: {
                legal_agreements: apiLegalAgreements,
                language: currentLanguage
            },
            requestName: generateRequestName('ah', 'post', 'updateuseragreements')
        });

        if (response.success) {
            // Extract user data from response (could be in response.data or response.user)
            // The response structure is: { success: true, data: { legal_agreements: {...}, ... } }
            const userData = response.data || response.user || response.data?.user;
            
            // Update compliance cache with the updated legal agreements
            // This ensures immediate compliance checks use the fresh data
            try {
                const { setCachedApiResponse } = await import('./legalAgreementsGuard');
                const accounts = this.msalInstance?.getAllAccounts();
                if (accounts && accounts.length > 0) {
                    const userId = accounts[0]?.homeAccountId || accounts[0]?.localAccountId || accounts[0]?.username;
                    if (userId) {
                        // Get legal_agreements from the correct location in response
                        const legalAgreements = userData?.legal_agreements || response.data?.legal_agreements;
                        
                        if (legalAgreements) {
                            // Map backend format to frontend format for cache
                            // Note: business_data_processing_agreement is ignored - we only use data_processing_agreement
                            const frontendAgreements = {
                                termsOfService: legalAgreements.terms_of_service?.agreed || false,
                                dataProcessingAgreement: legalAgreements.data_processing_agreement?.agreed || false,
                                privacyPolicy: legalAgreements.privacy_policy?.agreed || false,
                                endUserLicenseAgreement: legalAgreements.end_user_license_agreement?.agreed || false,
                                acceptableUse: legalAgreements.acceptable_use?.agreed || false
                            };
                            setCachedApiResponse(userId, frontendAgreements);
                            // Also clear the memory cache to force fresh fetch on next call
                            this.memoryCache.delete('user-legal-agreements');
                            logger.debug('API-CLIENT', 'Updated compliance cache and cleared memory cache with fresh legal agreements data', { userId });
                        } else {
                            logger.warn('API-CLIENT', 'No legal_agreements found in response to cache', {
                                hasUserData: !!userData,
                                hasResponseData: !!response.data
                            });
                        }
                    }
                }
            } catch (cacheError) {
                // Non-critical - log but don't fail
                logger.warn('API-CLIENT', 'Failed to update compliance cache', { error: cacheError.message });
            }
            
            // Reset circuit breakers for resources/tenants endpoints after legal agreements are accepted
            // This allows previously blocked requests to succeed immediately
            try {
                const { getApiService } = await import('./apiService');
                const apiService = getApiService();
                // Reset circuit breakers for common endpoints that were likely blocked
                apiService.resetCircuitBreaker('/v1/resources');
                apiService.resetCircuitBreaker('/v1/tenants');
                apiService.resetCircuitBreaker('listWorkspaces');
                apiService.resetCircuitBreaker('listResources');
                logger.info('API-CLIENT', 'Circuit breakers reset after legal agreements accepted');
            } catch (resetError) {
                // Non-critical - log but don't fail
                logger.warn('API-CLIENT', 'Failed to reset circuit breakers', { error: resetError.message });
            }
            
            return {
                success: true,
                data: {
                    message: 'Legal agreements updated successfully',
                    user: userData
                }
            };
        } else {
            // Log detailed error information for debugging
            const errorMessage = response.error || 'Failed to update legal agreements';
            logger.error('API-CLIENT', 'Legal agreements update failed', {
                error: errorMessage
            });
            throw new Error(errorMessage);
        }
    }

    /**
     * Get user legal agreements using secure APIM JWT token extraction
     * This method leverages APIM's JWT token validation and email extraction
     * instead of sending email in the request payload for better security
     * @returns {Promise<object>} Legal agreements data
     */
    async getUserLegalAgreements(forceFresh = false) {
        try {
            // Check memory cache first (10 second TTL)
            // BUT: Don't use cache if it's an auth-in-progress situation (requiresAuth) or if forceFresh is true
            if (!forceFresh) {
                const cached = this._getCached('user-legal-agreements');
                if (cached && !cached.requiresAuth) {
                    logger.debug('API-CLIENT', 'Using cached legal agreements from memory cache');
                    return cached;
                }
            } else {
                // Force fresh - clear memory cache
                this.memoryCache.delete('user-legal-agreements');
                logger.debug('API-CLIENT', 'Force fresh requested - cleared memory cache');
            }
            // If cached response has requiresAuth, ignore cache and make fresh request

            // Use the secure method that leverages APIM's JWT token extraction
            // Mark as per-page since this is called on every page load
            const result = await this.getUserOrCreateByEmail(generateRequestName('pp', 'post', 'checkuseragreements'));
            
            // Debug log to see what getUserOrCreateByEmail returned
            logger.info('API-CLIENT', 'getUserOrCreateByEmail result in getUserLegalAgreements', { 
                hasResult: !!result,
                success: result?.success,
                requiresAuth: result?.requiresAuth,
                hasData: !!result?.data,
                hasUser: !!result?.user,
                hasLegalAgreementsInData: !!(result?.data?.legal_agreements),
                hasLegalAgreementsInUser: !!(result?.user?.legal_agreements),
                hasLegalAgreementsInRoot: !!(result?.legal_agreements),
                userLegalAgreementsKeys: result?.user?.legal_agreements ? Object.keys(result.user.legal_agreements) : [],
                error: result?.error,
                resultKeys: result ? Object.keys(result) : [],
                fullResult: result
            });
            
            // CRITICAL: Check for requiresAuth FIRST, before trying to extract legal agreements
            // If authentication is in progress (token not ready), return early to allow access
            // Check multiple ways requiresAuth might be present
            if (result && result.requiresAuth === true) {
                logger.info('API-CLIENT', 'Authentication in progress - returning requiresAuth flag', { result });
                return {
                    success: false,
                    requiresAuth: true,
                    error: getApiUserMessage('authRequired')
                };
            }
            
            // Also check if result indicates auth failure (no success and no data)
            // This catches cases where requiresAuth might not be set but error indicates auth issue
            if (result && !result.success) {
                const errorMsg = (result.error || '').toLowerCase();
                const hasAuthError = errorMsg.includes('authentication') || 
                                   errorMsg.includes('token') || 
                                   errorMsg.includes('required') ||
                                   result.requiresAuth === true;
                
                // If no data and auth error, return requiresAuth
                // Check for absence of legal agreements data in all possible locations
                const hasNoLegalData = !result.data && 
                                      !result.legal_agreements && 
                                      !result.data?.legal_agreements &&
                                      !result.user?.legal_agreements &&
                                      !result.data?.data?.legal_agreements;
                
                if (hasNoLegalData && hasAuthError) {
                    logger.info('API-CLIENT', 'Authentication error detected - returning requiresAuth flag', { result, error: result.error });
                    return {
                        success: false,
                        requiresAuth: true,
                        error: result.error || getApiUserMessage('authRequired')
                    };
                }
            }
            
            // Handle multiple possible response structures
            let legalAgreementsData = null;
            let user = null;
            
            // CRITICAL FIX: Check result.user.legal_agreements FIRST since that's where the data actually is
            // Based on logs, hasUser: true and hasLegalAgreementsInUser: true
            if (result.user && result.user.legal_agreements) {
                const agreements = result.user.legal_agreements;
                // More lenient check - just verify it exists and is not null/undefined
                if (agreements !== null && agreements !== undefined) {
                    user = result.user;
                    legalAgreementsData = agreements;
                    logger.info('API-CLIENT', '✅ Found legal agreements in result.user.legal_agreements', {
                        keys: Object.keys(legalAgreementsData),
                        hasAcceptableUse: 'acceptable_use' in legalAgreementsData,
                        type: typeof legalAgreementsData,
                        isArray: Array.isArray(legalAgreementsData)
                    });
                }
            }
            
            // Fallback to other locations if user.legal_agreements not found
            if (!legalAgreementsData && result.data && result.data.legal_agreements && typeof result.data.legal_agreements === 'object' && !Array.isArray(result.data.legal_agreements)) {
                user = result.data;
                legalAgreementsData = result.data.legal_agreements;
                logger.info('API-CLIENT', 'Found legal agreements in result.data.legal_agreements');
            } else if (!legalAgreementsData && result.data && result.data.data && result.data.data.legal_agreements && typeof result.data.data.legal_agreements === 'object' && !Array.isArray(result.data.data.legal_agreements)) {
                user = result.data.data;
                legalAgreementsData = result.data.data.legal_agreements;
                logger.info('API-CLIENT', 'Found legal agreements in result.data.data.legal_agreements');
            } else if (!legalAgreementsData && result.legal_agreements && typeof result.legal_agreements === 'object' && !Array.isArray(result.legal_agreements)) {
                legalAgreementsData = result.legal_agreements;
                logger.info('API-CLIENT', 'Found legal agreements in result.legal_agreements');
            }
            
            if (!legalAgreementsData) {
                // Expected for new users (no user/tenant doc yet) - guide to onboarding, not an error
                logger.info('API-CLIENT', 'No legal agreements yet (expected for new user - guiding to onboarding)', {
                    hasResultData: !!result?.data,
                    resultSuccess: result?.success
                });
            }
            
            // Check if we have data regardless of result.success (some APIs don't set success flag)
            if (legalAgreementsData) {
                // Normalize: backend may return short keys (terms, dpa, eula, aup, privacy); frontend expects long keys
                const normalized = normalizeLegalAgreementsFromApi(legalAgreementsData);
                const legalAgreements = {
                    termsOfService: normalized.terms_of_service?.agreed === true,
                    dataProcessingAgreement: normalized.data_processing_agreement?.agreed === true,
                    privacyPolicy: normalized.privacy_policy?.agreed === true,
                    endUserLicenseAgreement: normalized.end_user_license_agreement?.agreed === true,
                    acceptableUse: normalized.acceptable_use?.agreed === true
                };
                
                // Log the mapped values for debugging
                logger.info('API-CLIENT', 'Mapped legal agreements to frontend format', {
                    termsOfService: legalAgreements.termsOfService,
                    dataProcessingAgreement: legalAgreements.dataProcessingAgreement,
                    privacyPolicy: legalAgreements.privacyPolicy,
                    endUserLicenseAgreement: legalAgreements.endUserLicenseAgreement,
                    acceptableUse: legalAgreements.acceptableUse,
                    allAccepted: Object.values(legalAgreements).every(v => v === true),
                    rawTermsOfService: legalAgreementsData.terms_of_service,
                    rawAcceptableUse: legalAgreementsData.acceptable_use
                });
                
                // Timestamps: backend may use short keys (terms, dpa, eula, aup, privacy)
                const raw = legalAgreementsData;
                const agreementTimestamps = {
                    termsOfService: raw.terms?.agreed_at || raw.terms_of_service?.agreed_at || null,
                    dataProcessingAgreement: raw.dpa?.agreed_at || raw.data_processing_agreement?.agreed_at || null,
                    privacyPolicy: raw.privacy?.agreed_at || raw.privacy_policy?.agreed_at || null,
                    endUserLicenseAgreement: raw.eula?.agreed_at || raw.end_user_license_agreement?.agreed_at || null,
                    acceptableUse: raw.aup?.agreed_at || raw.acceptable_use?.agreed_at || raw.acceptable_use_policy?.agreed_at || null
                };
                
                const response = {
                    success: true,
                    data: legalAgreements,
                    timestamps: agreementTimestamps,
                    user: user,
                    tenant: result.tenant
                };
                
                // Cache the result for 10 seconds
                this._setCached('user-legal-agreements', response);
                return response;
            }
            
            // CRITICAL: Before returning "all false", check if the original result had an auth error
            // If result.success is false and we couldn't extract legal agreements, it might be an auth issue
            if (result && !result.success) {
                const errorMsg = (result.error || '').toLowerCase();
                if (errorMsg.includes('authentication') || errorMsg.includes('token') || errorMsg.includes('required') || result.requiresAuth === true) {
                    logger.info('API-CLIENT', 'No legal agreements found but auth error detected - returning requiresAuth', { result, error: result.error });
                    return {
                        success: false,
                        requiresAuth: true,
                        error: result.error || getApiUserMessage('authRequired')
                    };
                }
            }
            
            // No legal agreements in response - expected for new user; guide to onboarding
            logger.info('API-CLIENT', 'No legal agreements in response (new user) - returning all false for onboarding', { 
                resultSuccess: result?.success 
            });
            return {
                success: true,
                data: {
                    termsOfService: false,
                    dataProcessingAgreement: false,
                    privacyPolicy: false,
                    endUserLicenseAgreement: false,
                    acceptableUse: false
                }
            };
            
        } catch (error) {
            // CRITICAL: Don't return misleading success:true with all false when API fails
            // This causes the caller to think user hasn't agreed when actually the API failed
            // Throw the error so the caller can handle it properly (e.g., use cached data)
            logger.error('API-CLIENT', 'Error fetching user legal agreements', { error: error.message });
            throw error;
        }
    }

    /**
     * Delete the current user's account (self-deletion)
     * This will delete the user from MongoDB, all user-tenant mappings, and Entra External ID
     * @returns {Promise<object>} Deletion result
     */
    async deleteSelfAccount() {
        try {
            const response = await this.apiRequest('/v1/users/self-delete', {
                method: 'POST',
                body: {},
                requestName: generateRequestName('ah', 'post', 'deleteselfaccount')
            });
            
            return response;
        } catch (error) {
            logger.error('API-CLIENT', 'Account deletion failed', { error: error.message });
            throw error;
        }
    }

    /**
     * Update user profile via Microsoft Graph API (Entra External ID)
     * @param {object} profileData - Profile data to update
     * @returns {Promise<object>} Updated profile
     */
    async updateProfileViaGraphAPI(profileData) {
        // Use shared MSAL instance instead of creating a new one
        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized. Please initialize the API client with an MSAL instance first.');
        }

        // Get current account from shared MSAL instance
        // Use centralized active account helper
        const account = getOrSelectActiveAccount(this.msalInstance);
        if (!account) {
            throw new Error('No active account selected. Please choose an account.');
        }

        // Acquire token for Microsoft Graph API
        const graphTokenRequest = {
            scopes: ['https://graph.microsoft.com/User.ReadWrite'],
            account: account
        };

        let accessToken;
        try {
            const tokenResponse = await this.msalInstance.acquireTokenSilent(graphTokenRequest);
            accessToken = tokenResponse.accessToken;
        } catch (error) {
            // No fallbacks: do not attempt popup/redirect here. Let the UI request re-auth explicitly.
            throw error;
        }

        // Update profile via Microsoft Graph API
        const graphApiUrl = 'https://graph.microsoft.com/v1.0/me';
        const updateData = {};

        // Only update display name
        if (profileData.displayName) {
            updateData.displayName = profileData.displayName;
        }

        const response = await fetch(graphApiUrl, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch (jsonError) {
                // If response is not JSON, use status text
                errorData = { error: { message: response.statusText || 'Unknown error' } };
            }
            throw new Error(`Graph API error: ${errorData.error?.message || 'Unknown error'}`);
        }

        // Microsoft Graph API PATCH requests return 204 No Content on success
        // We need to fetch the updated profile separately
        let updatedProfile;
        if (response.status === 204) {
            // Fetch the updated profile
            const getResponse = await fetch(graphApiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!getResponse.ok) {
                throw new Error('Failed to fetch updated profile');
            }
            
            try {
                updatedProfile = await getResponse.json();
            } catch (jsonError) {
                throw new Error('Failed to parse updated profile response');
            }
        } else {
            updatedProfile = await response.json();
        }
        
        return {
            success: true,
            data: {
                displayName: updatedProfile.displayName,
                email: updatedProfile.mail || updatedProfile.userPrincipalName,
                username: updatedProfile.userPrincipalName,
                accountStatus: updatedProfile.accountEnabled ? 'Active' : 'Inactive',
                message: 'Profile updated successfully via Entra External ID'
            }
        };
    }

    /**
     * Get full user profile from our backend API (Entra External ID)
     * @returns {Promise<object>} Full user profile
     */
    async getFullUserProfile() {
        logger.debug('API-CLIENT', 'Getting full user profile via backend API');
        
        try {
            // Use our backend API endpoint for profile data
            const response = await this.apiRequest('/v1/users/profile', {
                method: 'GET'
            });
            
            logger.debug('API-CLIENT', 'Full user profile loaded successfully');
            return response;
        } catch (error) {
            logger.error('API-CLIENT', 'Failed to load full user profile', { error: error.message });
            throw new Error(`Failed to load user profile: ${error.message}`);
        }
    }

    // =================================================================
    // RESOURCE API METHODS
    // =================================================================

    /**
     * List resources
     * @param {object} params - Query parameters
     * @returns {Promise<object>} Resources list
     */
    async listResources(params = {}) {
        const query = new URLSearchParams();
        if (params.workspaceId) {
            query.append('workspace_id', params.workspaceId);
        }
        if (params.tenant_id) {
            query.append('tenant_id', params.tenant_id);
        }
        if (params.status) {
            query.append('status', params.status);
        }
        const endpoint = `/v1/resources${query.toString() ? `?${query.toString()}` : ''}`;
        return this.apiRequest(endpoint, { 
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listresources')
        });
    }

    /**
     * Get resource details
     * @param {string} resourceId - Resource ID
     * @returns {Promise<object>} Resource details
     */
    async getResource(resourceId) {
        if (resourceId == null || String(resourceId).trim() === '' || String(resourceId) === 'undefined') {
            return Promise.resolve({ success: false, error: 'Resource ID is required', data: null });
        }
        const raw = await this.apiRequest(`/v1/resources/${resourceId}`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getresource')
        });
        const { normalizeGetResourceResponse } = await import('./apiClient/normalizeGetResourceResponse.js');
        return normalizeGetResourceResponse(raw);
    }

    /**
     * Resource overview (usage, knowledge counts, resource doc) — TigerBeetle-backed usage when usage_service is configured.
     * @param {string} resourceId
     */
    async getResourceOverview(resourceId) {
        if (resourceId == null || String(resourceId).trim() === '' || String(resourceId) === 'undefined') {
            return Promise.resolve({ success: false, error: 'Resource ID is required', data: null });
        }
        return this.apiRequest(`/v1/resources/${resourceId}/overview`, {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getresourceoverview')
        });
    }

    /**
     * Create resource
     * @param {object} resourceData - Resource data
     * @returns {Promise<object>} Created resource
     */
    async createResource(resourceData) {
        return this.apiRequest('/v1/resources', {
            method: 'POST',
            body: resourceData,
            requestName: generateRequestName('ah', 'post', 'createresource')
        });
    }

    /**
     * Update resource
     * @param {string} resourceId - Resource ID
     * @param {object} updates - Updates to apply
     * @returns {Promise<object>} Updated resource
     */
    async updateResource(resourceId, updates) {
        return this.apiRequest(`/v1/resources/${resourceId}`, {
            method: 'PUT',
            body: updates,
            requestName: generateRequestName('ah', 'put', 'updateresource')
        });
    }

    /**
     * Delete resource
     * @param {string} resourceId - Resource ID
     * @returns {Promise<object>} Deletion result
     */
    async deleteResource(resourceId) {
        return this.apiRequest(`/v1/resources/${resourceId}`, {
            method: 'DELETE',
            requestName: generateRequestName('ah', 'delete', 'deleteresource')
        });
    }

    /**
     * Get public products list (no authentication required)
     * Used by marketplace pages on auth, public, and bot sites
     * @returns {Promise<object>} Products list without price information
     */
    async getPublicProducts() {
        // Use v1 endpoint which is public and doesn't require auth
        return await this.apiRequest('/v1/payments/products', {
            method: 'GET',
            requestName: generateRequestName('pub', 'get', 'publicproducts')
        });
    }

    /**
     * Provision missing featured products
     * Checks which featured products the user doesn't have yet and auto-provisions them
     * @returns {Promise<object>} Provisioning result with newly provisioned products
     */
    /**
     * Provision missing featured products for a tenant. When tenantId is provided, sends it in the body so the backend provisions for that tenant.
     * When resaleResourceId is provided (e.g. user came from resale SDK), backend provisions that partner's free products instead of B-esper featured.
     * @param {string} [tenantId] - Optional tenant ID; when provided, sent in body and used by backend instead of X-Tenant-Id header.
     * @param {string} [resaleResourceId] - Optional resale resource ID; when provided, backend uses resale_{id} featured products (partner free product).
     */
    async provisionFeaturedProducts(tenantId, resaleResourceId = null) {
        const options = {
            method: 'POST',
            requestName: generateRequestName('res', 'post', 'provisionfeaturedproducts')
        };
        const body = {};
        if (tenantId && String(tenantId).trim()) {
            body.tenant_id = String(tenantId).trim();
        }
        if (resaleResourceId && String(resaleResourceId).trim()) {
            body.resale_resource_id = String(resaleResourceId).trim();
        }
        if (Object.keys(body).length > 0) {
            options.body = body;
        }
        return await this.apiRequest('/v1/provision-featured-products', options);
    }

    // =================================================================
    // COST POOL API METHODS
    // =================================================================

    /**
     * Check billing status across all accessible tenants.
     * Lightweight boolean: does ANY tenant have a valid payment method?
     * @returns {Promise<{success: boolean, has_payment_method: boolean, has_cost_pool?: boolean, has_legal_agreements?: boolean, cost_pool_id?: string|null}>}
     */
    async getBillingStatus() {
        const BILLING_CACHE_TTL_MS = 30000; // 30s to reduce duplicate calls
        const cached = this._getCached('billing_status');
        if (cached) return cached;

        const result = await this.apiRequest('/v1/billing/status', {
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'billingstatus')
        });
        if (result && result.success !== false) {
            this._setCached('billing_status', result, BILLING_CACHE_TTL_MS);
        }
        return result;
    }

    /**
     * List cost pools
     * @param {object} params - Query parameters
     * @returns {Promise<object>} Cost pools list
     */
    async listCostPools(params = {}) {
        const query = new URLSearchParams();
        if (params.tenantId) {
            query.append('tenant_id', params.tenantId);
        }
        if (params.workspaceId) {
            query.append('workspace_id', params.workspaceId);
        }
        if (params.requirePaymentMethod) {
            query.append('require_payment_method', 'true');
        }
        const endpoint = `/v1/cost-pools${query.toString() ? `?${query.toString()}` : ''}`;
        return this.apiRequest(endpoint, { 
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'listcostpools')
        });
    }

    /**
     * Get cost pool details
     * @param {string} costPoolId - Cost pool ID
     * @param {string} tenantId - Tenant ID (required for authorization)
     * @returns {Promise<object>} Cost pool details
     */
    async getCostPool(costPoolId, tenantId) {
        const endpoint = `/v1/cost-pools/${costPoolId}${tenantId ? `?tenant_id=${tenantId}` : ''}`;
        return this.apiRequest(endpoint, {
            method: 'GET'
        });
    }

    /**
     * Create cost pool
     * @param {object} costPoolData - Cost pool data
     * @returns {Promise<object>} Created cost pool
     */
    async createCostPool(costPoolData) {
        const requestOptions = {
            method: 'POST',
            body: costPoolData,
            timeout: 360000 // 6 minutes - PDF generation can take up to 3 minutes, plus buffer for email sending
        };
        logger.debug('API-CLIENT', 'Calling apiRequest for cost pool creation', { timeout: requestOptions.timeout });
        return this.apiRequest('/v1/cost-pools', requestOptions);
    }

    /**
     * Accept Business DPA for a cost pool
     * @param {string} costPoolId - Cost pool ID
     * @param {string} tenantId - Tenant ID
     * @param {object} acceptanceData - Acceptance data (company_legal_name, company_address, vat_id, dpa_version)
     * @returns {Promise<object>} Acceptance result
     */
    async acceptBusinessDpa(costPoolId, tenantId, acceptanceData) {
        return this.apiRequest(`/v1/cost-pools/${costPoolId}/business-dpa/accept?tenant_id=${tenantId}`, {
            method: 'POST',
            body: acceptanceData
        });
    }

    /**
     * Update cost pool
     * @param {string} costPoolId - Cost pool ID
     * @param {object} updates - Updates to apply
     * @returns {Promise<object>} Updated cost pool
     */
    async updateCostPool(costPoolId, updates) {
        return this.apiRequest(`/v1/cost-pools/${costPoolId}`, {
            method: 'PUT',
            body: updates
        });
    }

    /**
     * Delete cost pool
     * @param {string} costPoolId - Cost pool ID
     * @param {string} tenantId - Tenant ID (required)
     * @returns {Promise<object>} Deletion result
     */
    async deleteCostPool(costPoolId, tenantId) {
        if (!tenantId) {
            throw new Error('tenant_id is required to delete a cost pool');
        }
        return this.apiRequest(`/v1/cost-pools/${costPoolId}?tenant_id=${encodeURIComponent(tenantId)}`, {
            method: 'DELETE'
        });
    }

    // =================================================================
    // PUBLIC STATISTICS API METHODS (Anonymous access)
    // =================================================================

    /**
     * Get public statistics (anonymous access with smart caching)
     * @returns {Promise<object>} Public statistics data
     */
    async getPublicStatistics() {
        // Clear any old cached data that might contain hardcoded fallback values
        const cacheKey = 'public_statistics_cache';
        localStorage.removeItem(cacheKey);
        
        // Always fetch fresh data from API

        // This endpoint doesn't require authentication - use public API
        const publicApiUrl = await getApiEndpoint('public');
        const normalizedPublicApiUrl = `${publicApiUrl || ''}`.replace(/\/+$/, '');
        const apiBaseUrl = normalizedPublicApiUrl.endsWith('/api')
            ? normalizedPublicApiUrl
            : `${normalizedPublicApiUrl}/api`;
        const endpoint = `/v1/statistics`;
        const url = `${apiBaseUrl}${endpoint}`;
        
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                // No authentication required for public stats
            });

            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                logger.error('API-CLIENT', 'Failed to parse statistics response', { error: parseError.message });
                
                // Return fallback immediately instead of trying again
                const fallbackData = {
                    success: true,
                    statistics: {
                        facilitated_sessions: 0,
                        display_text: "0",
                        data_source: "fallback_parse_error"
                    }
                };
                
                // Cache the fallback for future use
                localStorage.setItem(cacheKey, JSON.stringify({
                    data: fallbackData,
                    timestamp: Date.now()
                }));
                
                return fallbackData;
            }

            if (response.ok) {
                logger.debug('API-CLIENT', 'Public statistics loaded successfully');
                
                // Don't cache to ensure fresh data on every load
                return data;
            } else {
                logger.warn('API-CLIENT', 'Statistics API returned error', { status: response.status });
                
                // Return fallback with 0 as requested
                const fallbackData = {
                    success: true,
                    statistics: {
                        facilitated_sessions: 0,
                        display_text: "0",
                        data_source: "fallback_api_error"
                    }
                };
                
                // Don't cache fallback data
                return fallbackData;
            }

        } catch (error) {
            logger.error('API-CLIENT', 'Public statistics request failed', { error: error.message });
            
            // Return 0 fallback as requested instead of trying again
            const fallbackData = {
                success: true,
                statistics: {
                    facilitated_sessions: 0,
                    display_text: "0",
                    data_source: "fallback_network_error"
                }
            };
            
            // Don't cache fallback data
            return fallbackData;
        }
    }

    /**
     * Get public SLA statistics
     * @param {string} period - Time period: 'hour', 'day', 'month', 'year' (default: 'day')
     * @returns {Promise<any>} SLA statistics with endpoint data
     */
    async getPublicSla(period = 'day') {
        const publicApiUrl = await getApiEndpoint('public');
        const isLocalhost = publicApiUrl.includes('localhost') || publicApiUrl.includes('127.0.0.1');
        const apiBase = isLocalhost ? `${publicApiUrl}/api` : publicApiUrl;
        // Avoid double /api/api/ when base already ends with /api
        const hasApiPrefix = apiBase && (apiBase.endsWith('/api') || apiBase.includes('/api/'));
        const path = hasApiPrefix ? '/v1/sla' : '/api/v1/sla';
        const url = `${apiBase}${path}?period=${period}`;
        
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                return data;
            } else {
                logger.warn('API-CLIENT', 'SLA API returned error', { status: response.status });
                return { success: false, endpoints: {} };
            }
        } catch (error) {
            logger.error('API-CLIENT', 'Public SLA request failed', { error: error.message });
            return { success: false, endpoints: {} };
        }
    }

    // Application User Management Methods
    
    /**
     * Get all application users for the authenticated user
     * @returns {Promise<any>} List of application users
     */
    async getApplicationUsers() {
        logger.debug('API-CLIENT', 'Fetching application users');
        return await this.apiRequest('/v1/app-users');
    }

    /**
     * Create a new application user
     * @param {object} appUserData - Application user data
     * @returns {Promise<any>} Created application user
     */
    async createApplicationUser(appUserData) {
        logger.debug('API-CLIENT', 'Creating application user', { hasData: !!appUserData });
        return await this.apiRequest('/v1/app-users', {
            method: 'POST',
            body: appUserData
        });
    }

    /**
     * Get a specific application user by ID
     * @param {string} appUserId - Application user ID
     * @returns {Promise<any>} Application user details
     */
    async getApplicationUser(appUserId) {
        logger.debug('API-CLIENT', 'Fetching application user', { appUserId });
        return await this.apiRequest(`/v1/app-users/${appUserId}`);
    }

    /**
     * Update an application user
     * @param {string} appUserId - Application user ID
     * @param {object} updateData - Update data
     * @returns {Promise<any>} Updated application user
     */
    async updateApplicationUser(appUserId, updateData) {
        logger.debug('API-CLIENT', 'Updating application user', { appUserId });
        return await this.apiRequest(`/v1/app-users/${appUserId}`, {
            method: 'PUT',
            body: updateData
        });
    }

    /**
     * Delete an application user
     * @param {string} appUserId - Application user ID
     * @returns {Promise<any>} Deletion result
     */
    async deleteApplicationUser(appUserId) {
        logger.debug('API-CLIENT', 'Deleting application user', { appUserId });
        return await this.apiRequest(`/v1/app-users/${appUserId}`, {
            method: 'DELETE'
        });
    }

    /**
     * Generate a new API key for an application user
     * @param {string} appUserId - Application user ID
     * @param {object} keyOptions - API key options
     * @returns {Promise<any>} Generated API key
     */
    async generateApiKey(appUserId, keyOptions = {}) {
        logger.debug('API-CLIENT', 'Generating API key for application user', { appUserId });
        return await this.apiRequest(`/v1/app-users/${appUserId}/api-keys`, {
            method: 'POST',
            body: keyOptions
        });
    }

    /**
     * List API keys for an application user (sanitized; no hashes)
     * @param {string} appUserId
     * @returns {Promise<any>}
     */
    async listApiKeys(appUserId) {
        logger.debug('API-CLIENT', 'Listing API keys for application user', { appUserId });
        try {
            const result = await this.apiRequest(`/v1/app-users/${appUserId}/api-keys`, {
                method: 'GET'
            });
            logger.debug('API-CLIENT', 'API keys list result', { appUserId, hasData: !!result.data });
            return result;
        } catch (error) {
            logger.error('API-CLIENT', 'Error listing API keys', { appUserId, error: error.message });
            // If it's a 404, return empty array instead of throwing
            if (error.status === 404) {
                logger.debug('API-CLIENT', 'API keys endpoint not found (404), returning empty array', { appUserId });
                return { success: true, data: [] };
            }
            throw error;
        }
    }

    /**
     * Revoke (deactivate) an API key for an application user
     * @param {string} appUserId
     * @param {string} keyId
     * @returns {Promise<any>}
     */
    async revokeApiKey(appUserId, keyId) {
        logger.debug('API-CLIENT', 'Revoking API key', { appUserId, keyId });
        try {
            return await this.apiRequest(`/v1/app-users/${appUserId}/api-keys/${keyId}/revoke`, {
                method: 'POST'
            });
        } catch (error) {
            logger.error('API-CLIENT', 'Error revoking API key', { appUserId, keyId, error: error.message });
            if (error.status === 404) {
                logger.debug('API-CLIENT', 'API key revoke endpoint not found (404)', { appUserId, keyId });
                return { success: false, error: 'API key not found' };
            }
            throw error;
        }
    }

    /**
     * Delete an API key from an application user
     * @param {string} appUserId
     * @param {string} keyId
     * @returns {Promise<any>}
     */
    async deleteApiKey(appUserId, keyId) {
        logger.debug('API-CLIENT', 'Deleting API key', { appUserId, keyId });
        try {
            return await this.apiRequest(`/v1/app-users/${appUserId}/api-keys/${keyId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            logger.error('API-CLIENT', 'Error deleting API key', { appUserId, keyId, error: error.message });
            if (error.status === 404) {
                logger.debug('API-CLIENT', 'API key delete endpoint not found (404)', { appUserId, keyId });
                return { success: false, error: 'API key not found' };
            }
            throw error;
        }
    }

    /**
     * Get application users filtered by tenant
     * @param {string} tenantId - Tenant ID to filter by
     * @returns {Promise<any>} List of application users for the tenant
     */
    async getApplicationUsersByTenant(tenantId) {
        logger.debug('API-CLIENT', 'Fetching application users for tenant', { tenantId });
        return await this.apiRequest(`/v1/app-users?tenant_id=${tenantId}`);
    }

    /**
     * Get usage stats for a specific application user
     * @param {string} appUserId - Application user ID
     * @param {string} tenantId - Tenant ID
     * @returns {Promise<any>} Usage statistics
     */
  async getApplicationUserUsage(appUserId, tenantId) {
    logger.debug('API-CLIENT', 'Fetching usage stats for application user', { appUserId, tenantId });
    return await this.apiRequest(`/v1/app-users/${appUserId}/usage?tenant_id=${tenantId}`);
  }


    // =================================================================
    // BOT MANAGEMENT API METHODS (APIM Public)
    // =================================================================

    /**
     * Make a bot management API call to APIM public
     * @param {string} endpoint - API endpoint path (e.g., 'bot-details', 'update_instructions')
     * @param {string} method - HTTP method
     * @param {object} body - Request body
     * @returns {Promise<object>} API response
     */
    async botManagementApiCall(endpoint, method = 'GET', body = null) {
        const pubConfig = await getPublicApiConfig();
        let fullEndpoint = `/v1/bot-management/${endpoint}`.replace(/\/+/g, '/');
        
        const url = `${pubConfig.baseUrl}${fullEndpoint}`;

        logger.debug('API-CLIENT', 'Bot Management API call', { method, endpoint });

        const token = await this.getApiAccessToken();
        if (!token) {
            // Return gracefully instead of throwing to prevent error spam
            return {
                success: false,
                error: getApiUserMessage('authRequired'),
                requiresAuth: true
            };
        }

        const requestOptions = {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            ...(body && { body: JSON.stringify(body) })
        };

        try {
            const response = await fetch(url, requestOptions);
            
            if (!response.ok) {
                throw new Error(`Bot Management API Error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            logger.debug('API-CLIENT', 'Bot Management API Success', { method, endpoint });
            return data;
        } catch (error) {
            logger.error('API-CLIENT', 'Bot Management API Error', { method, endpoint, error: error.message });
            throw error;
        }
    }

    // Public APIM generic call (e.g., payment endpoints)
    async publicApiCall(endpoint, method = 'POST', body = null) {
        const pubConfig = await getPublicApiConfig();
        let fullEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        
        const hasApiInBase = pubConfig.baseUrl && (pubConfig.baseUrl.endsWith('/api') || pubConfig.baseUrl.includes('/api/'));
        if (hasApiInBase && fullEndpoint.startsWith('/api/')) {
            fullEndpoint = fullEndpoint.slice(4);
        } else if (!hasApiInBase && !fullEndpoint.startsWith('/api/')) {
            fullEndpoint = `/api${fullEndpoint}`;
        }
        
        const url = `${pubConfig.baseUrl}${fullEndpoint}`;

        const token = await this.getApiAccessToken();
        if (!token) {
            // Return gracefully instead of throwing to prevent error spam
            return {
                success: false,
                error: getApiUserMessage('authRequired'),
                requiresAuth: true
            };
        }

        const requestOptions = {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            ...(body && { body: JSON.stringify(body) })
        };

        const response = await fetch(url, requestOptions);
        const data = await response.json().catch(() => ({ success: false, error: 'Invalid JSON' }));
        if (!response.ok) {
            const err = new Error(data?.error || `Public API error: ${response.status}`);
            err.status = response.status;
            err.data = data;
            throw err;
        }
        return data;
    }

    // Payment helpers (Stripe)
    async createStripePortalSession(customerId, returnUrl) {
        return this.publicApiCall('/payment/create-customer-portal-session', 'POST', {
            customer_id: customerId,
            return_url: returnUrl
        });
    }

    async listCustomerPaymentMethods(customerId, page = 1, limit = 10) {
        return this.publicApiCall('/payment/list-payment-methods', 'POST', {
            customer_id: customerId,
            page,
            limit
        });
    }

    async setDefaultPaymentMethod(customerId, paymentMethodId) {
        return this.publicApiCall('/payment/set-default-payment-method', 'POST', {
            customer_id: customerId,
            payment_method_id: paymentMethodId
        });
    }

    async deletePaymentMethod(paymentMethodId) {
        return this.publicApiCall('/payment/delete-payment-method', 'POST', {
            payment_method_id: paymentMethodId
        });
    }

    async listCustomerInvoices(customerId, page = 1, limit = 10) {
        return this.publicApiCall('/payment/list-invoices', 'POST', {
            customer_id: customerId,
            page,
            limit
        });
    }

    async listCustomerCharges(customerId, page = 1, limit = 10) {
        return this.publicApiCall('/payment/list-charges', 'POST', {
            customer_id: customerId,
            page,
            limit
        });
    }

    /**
     * Get bot details from APIM public
     * @param {string} botId - Bot ID
     * @param {string} tenantId - Tenant ID
     * @returns {Promise<object>} Bot details
     */
    async getBotDetails(botId, tenantId) {
        logger.debug('API-CLIENT', 'Getting bot details from APIM public', { botId, tenantId });
        return await this.botManagementApiCall('bot-details', 'POST', {
            resource_id: botId,
            tenant_id: tenantId
        });
    }

    /**
     * Update bot instructions via APIM public
     * @param {string} botId - Bot ID
     * @param {string} managementId - Management ID
     * @param {string} instructions - Bot instructions
     * @returns {Promise<object>} Update result
     */
    async updateBotInstructions(botId, managementId, instructions) {
        logger.debug('API-CLIENT', 'Updating bot instructions via APIM public', { botId, managementId });
        return await this.botManagementApiCall('update_instructions', 'PATCH', {
            resource_id: botId,
            management_id: managementId,
            instructions
        });
    }

    /**
     * Update bot styling via APIM public
     * @param {string} botId - Bot ID
     * @param {string} managementId - Management ID
     * @param {object} styling - Styling configuration
     * @returns {Promise<object>} Update result
     */
    async updateBotStyling(botId, managementId, styling) {
        logger.debug('API-CLIENT', 'Updating bot styling via APIM public', { botId, managementId });
        return await this.botManagementApiCall('update_styling', 'PATCH', {
            resource_id: botId,
            management_id: managementId,
            styling
        });
    }

    /**
     * Update welcome messages via APIM public
     * @param {string} botId - Bot ID
     * @param {string} managementId - Management ID
     * @param {object} welcomeMessages - Welcome messages
     * @returns {Promise<object>} Update result
     */
    async updateWelcomeMessages(botId, managementId, welcomeMessages) {
        logger.debug('API-CLIENT', 'Updating welcome messages via APIM public', { botId, managementId });
        return await this.botManagementApiCall('update_welcome_messages', 'PATCH', {
            resource_id: botId,
            management_id: managementId,
            welcome_messages: welcomeMessages
        });
    }

    /**
     * Add knowledge to bot via APIM public
     * @param {string} botId - Bot ID
     * @param {string} managementId - Management ID
     * @param {object} knowledgeData - Knowledge data
     * @returns {Promise<object>} Add result
     */
    async addKnowledge(botId, managementId, knowledgeData) {
        logger.debug('API-CLIENT', 'Adding knowledge via APIM public', { botId, managementId });
        return await this.botManagementApiCall('add_knowledge', 'POST', {
            resource_id: botId,
            management_id: managementId,
            ...knowledgeData
        });
    }

    /**
     * List bot conversations via APIM public
     * @param {string} botId - Bot ID
     * @param {string} managementId - Management ID
     * @param {object} options - Query options
     * @returns {Promise<object>} Conversations list
     */
    async listBotConversations(botId, managementId, options = {}) {
        logger.debug('API-CLIENT', 'Listing bot conversations via APIM public', { botId, managementId });
        return await this.botManagementApiCall('list_conversations', 'POST', {
            resource_id: botId,
            management_id: managementId,
            action: 'list',
            ...options
        });
    }

    /**
     * HTTP GET request
     * @param {string} endpoint - API endpoint
     * @param {object} params - Query parameters
     * @returns {Promise<object>} Response data
     */
    async get(endpoint, params = {}) {
        const queryString = new URLSearchParams(params).toString();
        const url = queryString ? `${endpoint}?${queryString}` : endpoint;
        return await this.apiRequest(url, { method: 'GET' });
    }

    /**
     * List accessible tenants (id, name) for dropdowns
     * @returns {Promise<Array<{id:string,name:string}>>}
     */
    async listTenants() {
        try {
            // Use relative endpoint - apiRequest will add /api/ prefix for localhost
            const res = await this.get('/v1/tenants', { scope: 'accessible', fields: 'id,name' });
            const rows = (res && res.data) || res || [];
            return rows.map(r => ({ id: r.id || r.tenant_id || r.handle || r, name: r.name || r.display_name || r.title || r.id }));
        } catch (e) {
            logger.warn('API-CLIENT', 'Unable to list tenants for dropdown', { error: e.message });
            return [];
        }
    }

    /**
     * HTTP POST request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request body data
     * @returns {Promise<object>} Response data
     */
    async post(endpoint, data = {}, options = {}) {
        return await this.apiRequest(endpoint, {
            method: 'POST',
            body: data,
            ...options,
        });
    }

    /**
     * HTTP POST request with FormData (for file uploads)
     * @param {string} endpoint - API endpoint
     * @param {FormData} formData - FormData object
     * @returns {Promise<object>} Response data
     */
    async postFormData(endpoint, formData) {
        await this.ensureInitialized();
        await this.waitForAuthentication();
        
        const token = await this.getAccessToken();
        if (!token) {
            throw new Error(getApiUserMessage('authRequired'));
        }
        
        const config = await getApiConfig();
        
        // Build full URL (same logic as apiRequest)
        const isAbsolute = /^https?:\/\//i.test(endpoint);
        const isLocalhost = config.baseUrl && (config.baseUrl.includes('localhost') || config.baseUrl.includes('127.0.0.1'));
        const isApim = config.baseUrl && config.baseUrl.includes('azure-api.net');
        // eslint-disable-next-line no-unused-vars
        const baseUrlHasApi = config.baseUrl && config.baseUrl.includes('/api');
        
        let fullUrl;
        if (isAbsolute) {
            fullUrl = endpoint;
        } else {
            let baseUrl = config.baseUrl || '';
            if (isLocalhost && !endpoint.startsWith('/api/')) {
                // For localhost, add /api/ prefix if not present
                fullUrl = `${baseUrl}/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
            } else if (isApim) {
                // For APIM, baseUrl should already have /api, so don't add it again
                fullUrl = `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
            } else {
                // Fallback
                fullUrl = `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
            }
        }
        
        // Don't set Content-Type for FormData - browser will set it with boundary
        const headers = {
            'Authorization': `Bearer ${token}`
        };
        
        try {
            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: headers,
                body: formData
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
                return {
                    success: false,
                    error: errorData.error || `HTTP ${response.status} ${response.statusText}`
                };
            }
            
            const responseData = await response.json();
            return responseData;
        } catch (error) {
            logger.error('API-CLIENT', 'FormData upload failed', { error: error.message });
            return {
                success: false,
                error: error.message || 'Upload failed'
            };
        }
    }

    /**
     * HTTP PUT request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request body data
     * @returns {Promise<object>} Response data
     */
    async put(endpoint, data = {}, requestOptions = {}) {
        return await this.apiRequest(endpoint, { method: 'PUT', body: data, ...requestOptions });
    }

    /**
     * HTTP PATCH request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request data
     * @returns {Promise<object>} Response data
     */
    async patch(endpoint, data = {}) {
        return await this.apiRequest(endpoint, { method: 'PATCH', body: data });
    }

    /**
     * HTTP DELETE request
     * @param {string} endpoint - API endpoint
     * @returns {Promise<object>} Response data
     */
    async delete(endpoint) {
        return await this.apiRequest(endpoint, { method: 'DELETE' });
    }

    // User Settings Management
    async getUserSettings() {
        const endpoint = '/v1/users/settings';
        return await this.apiRequest(endpoint, { 
            method: 'GET',
            requestName: generateRequestName('ah', 'get', 'getusersettings')
        });
    }

    async updateUserSettings(settings) {
        const endpoint = '/v1/users/settings';
        return await this.apiRequest(endpoint, { 
            method: 'PUT',
            requestName: generateRequestName('ah', 'put', 'updateusersettings'), 
            data: settings 
        });
    }

    async setPreferredTenant(tenantId) {
        return await this.updateUserSettings({ preferred_tenant_id: tenantId });
    }
}

// Global API client instance
let apiClient = null;

/**
 * Get or create global API client instance
 * @returns {CustomerPortalApiClient} API client instance
 */
export function getApiClient() {
    if (!apiClient) {
        apiClient = new CustomerPortalApiClient();
    }
    return apiClient;
}

// Analytics convenience wrappers
export async function getTenantHierarchyAnalytics({ mode = 'hierarchy', start, end, rootTenantId } = {}) {
    const client = getApiClient();
    const params = {};
    if (mode) params.mode = mode;
    if (start) params.start = start;
    if (end) params.end = end;
    if (rootTenantId) params.root_tenant_id = rootTenantId;
    return client.get('/v1/analytics/tenant-hierarchy', params);
}

export async function getTenantUsersAnalytics({ tenantId, start, end }) {
    const client = getApiClient();
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get(`/v1/analytics/tenant/${tenantId}/users`, params);
}

/**
 * Initialize API client with MSAL instance
 * @param {object} msalInstance - MSAL instance
 * @returns {CustomerPortalApiClient} Initialized API client
 */
export function initializeApiClient(msalInstance) {
    const client = getApiClient();
    client.initialize(msalInstance);
    return client;
}

/**
 * Convenience function for making authenticated API calls
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Request options
 * @returns {Promise<any>} API response
 */
export async function callApiWithAuth(endpoint, options = {}) {
    const client = getApiClient();
    const method = options.method || 'GET';
    
    switch (method.toUpperCase()) {
        case 'GET':
            return await client.get(endpoint, options.params);
        case 'POST':
            return await client.post(endpoint, options.data);
        case 'PUT':
            return await client.put(endpoint, options.data);
        case 'PATCH':
            return await client.patch(endpoint, options.data);
        case 'DELETE':
            return await client.delete(endpoint);
        default:
            throw new Error(`Unsupported HTTP method: ${method}`);
    }
}

export default CustomerPortalApiClient;